import type { Dictionary } from "@/lib/i18n/dictionaries/en";

const es: Dictionary = {
  // ---- /citation-styles/alphabetical/<letter> ----
  styleDirectory: {
    notFoundTitle: "Directorio de estilos de citación no encontrado",
    metaTitle: "Estilos de citación que empiezan por {label}",
    metaDescription:
      "Explora {count} estilos Citation Style Language que empiezan por {label}. Abre cualquier estilo para ver ejemplos reales de citas y un generador gratuito.",
    ogSubtitle: "{count} estilos con ejemplos y generadores propios.",
    ogKind: "Directorio A–Z",
    schemaName: "Estilos de citación que empiezan por {label}",
    schemaDescription:
      "Directorio alfabético de {count} estilos Citation Style Language que empiezan por {label}.",
    breadcrumbStyles: "Estilos de citación",
    heading: "Estilos de citación que empiezan por {label}",
    intro:
      "Explora los {count} estilos de esta parte del catálogo CSL. Cada página incluye ejemplos formateados del propio estilo y un generador de citas gratuito.",
    navLabel: "Estilos de citación de la A a la Z",
    listHeading: "Todos los estilos que empiezan por {label}",
  },
  nav: {
    tools: "Herramientas",
    guides: "Guías",
    styles: "Todos los estilos",
    bibliography: "Mi bibliografía",
    search: "Buscar",
    glossary: "Glosario",
    home: "Inicio",
    menu: "Menú",
  },

  home: {
    title: "Generador de citas gratuito",
    subtitle: "Actualizado para 2026",
    intro:
      "Pega un DOI, una URL o una entrada BibTeX — o busca un libro — y obtén una cita con el formato correcto en APA, MLA, Chicago, Vancouver, Harvard y miles de estilos más. Totalmente gratis, sin suscripción de pago y sin registro.",
    browseAllStyles: "Ver los {count} estilos de cita →",
    featuresHeading: "Todo lo que necesitas para citar",
    featureStyles: "{count} estilos",
    featureStylesDetail: "APA, MLA, Chicago y más",
    featureBibliographies: "Varias bibliografías",
    featureBibliographiesDetail: "Organiza por trabajo",
    featureBookSearch: "Búsqueda de libros",
    featureBookSearchDetail: "Por título o autor",
    featureExport: "Exporta a donde quieras",
    featureExportDetail: "BibTeX, Word, RIS, CSV",
    featureDarkMode: "Modo oscuro",
    featureDarkModeDetail: "Cómodo para la vista",
    featureNoSignup: "Sin registro",
    featureNoSignupDetail: "Gratis siempre, sin suscripción de pago",
    aboutHeading:
      "Un generador de citas gratuito para estudiantes e investigadores",
    aboutBody:
      "CitationEasy admite más de 10.000 estilos de cita académicos. Ya necesites APA para psicología, MLA para literatura, Chicago para historia o Vancouver para revistas médicas, el formato procede de la definición oficial del propio estilo, no de una aproximación.",
    howItWorksHeading: "Cómo funciona",
    howItWorksStep1:
      "Pega un DOI, una URL, un ISBN, un identificador de PubMed o de arXiv, o una entrada BibTeX; también puedes buscar un libro por su título.",
    howItWorksStep2:
      "Elige tu estilo de cita. Escribe para buscar entre los {count} disponibles.",
    howItWorksStep3:
      "Copia la referencia terminada o guárdala en una bibliografía y expórtala a Word, BibTeX o RIS.",
    privacyNote:
      "Tu bibliografía se guarda en tu propio navegador. Sin cuenta y sin subir nada a ningún servidor.",
  },

  hubs: {
    toolsTitle: "Herramientas gratuitas de escritura y citación",
    toolsIntro:
      "{count} herramientas que funcionan en el navegador para escribir y citar: generadores para cada tipo de fuente, contadores de palabras y caracteres, análisis de legibilidad, formateadores y conversores. Todas gratuitas e instantáneas.",
    stylesTitle: "Explora todos los estilos de cita",
    stylesIntro:
      "Los {count} estilos del proyecto Citation Style Language, con búsqueda y filtros por país, materia y disciplina académica.",
    guidesTitle: "Guías de citación y escritura académica",
    guidesIntro:
      "Guías detalladas sobre cómo citar cada tipo de fuente, cómo evaluar lo que encuentras y cómo escribir mejor.",
    howToCiteTitle: "Cómo citar cualquier fuente",
    howToCiteIntro:
      "Páginas paso a paso que combinan cada tipo de fuente habitual con los principales estilos de cita, cada una con un ejemplo real resuelto.",
    faqTitle: "Preguntas frecuentes sobre citación",
    faqIntro:
      "Respuestas breves y precisas a las dudas que de verdad tienen los estudiantes al citar.",
    glossaryTitle: "Glosario de citación",
    glossaryIntro:
      "Definiciones claras de los términos que aparecen en los manuales de estilo y en los enunciados de los trabajos.",
    openHub: "Abrir",
  },

  styleLanding: {
    metaTitle: "Generador de citas {style} gratuito",
    metaDescription:
      "Genera citas en {style} gratis. Pega un DOI, una URL, un ISBN o BibTeX y obtén la referencia y la cita en el texto con el formato {style} correcto.",
    heading: "Generador de citas {style}",
    intro:
      "Crea citas {style} precisas para libros, artículos de revista y páginas web con un clic. Gratis y sin registro.",
    aboutHeading: "Sobre {style}",
    whenToUseHeading: "Cuándo usar {style}",
    factsHeading: "{style} de un vistazo",
    factEdition: "Edición",
    factSystem: "Sistema de cita",
    factInText: "Formato en el texto",
    factReferenceList: "Nombre de la lista",
    factFields: "Habitual en",
    systemAuthorDate: "Autor–fecha",
    systemNumbered: "Numérico",
    systemFootnotes: "Notas al pie",
    howToHeading: "Cómo usar este generador de {style}",
    howToStep1:
      "Pega una URL, un DOI, un ISBN o una entrada BibTeX en el cuadro de arriba, o busca un libro por su título.",
    howToStep2:
      "{style} ya está seleccionado en esta página, así que puedes generar directamente.",
    howToStep3:
      "Copia el resultado en tu documento, o guárdalo y exporta la lista completa más tarde.",
    relatedHeading: "Más recursos sobre {style}",
    englishOnlyNote:
      "Las guías detalladas de {style} y los ejemplos por tipo de fuente están disponibles en inglés.",
  },

  // The per-style pages at `/<locale>/citation-style/<slug>` — one per catalog style.
  //
  // The formatted examples the page is built around (the style's own output for a
  // fixed journal article, book and website) and the style's own name stay in
  // `src/data/citation-styles.ts` and `src/data/style-examples-generated.json`. They
  // are language-neutral: a citation rendered by the style's own CSL definition looks
  // the same whatever language the reader speaks, and "translating" one would make the
  // page wrong.
  //
  // The rest of what `src/data/**` supplies to these pages is NOT like that — see the
  // `generic*`, `discipline*` and `internationalLabel` keys below.
  citationStylePage: {
    notFoundTitle: "Estilo de cita no encontrado",
    metaTitle: "Generador de citas {style} - Formato {style} gratis",
    metaFormatAndFields:
      "{style} es un estilo {format} que se usa en {fields}.",
    metaFormatOnly: "{style} es un estilo {format}.",
    metaFieldsOnly: "{style} se usa en {fields}.",
    metaPromiseNote:
      "Consulta una nota al pie real en {style} y genera la tuya gratis.",
    metaPromiseExamples:
      "Consulta ejemplos reales de citas en {style} para un artículo de revista, un libro y una página web, y genera las tuyas gratis.",
    ogKindFormat: "Estilo {format}",
    ogKindDefault: "Estilo de cita",
    // Joins the style's discipline list: "psychology, education and nursing".
    disciplineConjunction: "y",
    // ---- Data-derived facts that are nonetheless mail-merged prose ----
    //
    // Every entry in `src/data/citation-styles-generated.ts` carries exactly the
    // two sentences below with their own name spliced in, exactly one region value
    // ("International" — that is `internationalLabel` above, reused rather than
    // duplicated), and discipline tokens drawn from a closed 26-word vocabulary. None
    // of that is a citation, so none of it is language-neutral the way the formatted
    // examples are. Left in `src/data/**` it renders English facts inside translated
    // chrome, which is precisely the hybrid page this tree exists to avoid.
    //
    // The page substitutes the two templates ONLY when a style's stored text is
    // byte-identical to the English template with its own name interpolated, so the
    // ~100 hand-curated styles keep their bespoke English descriptions instead of
    // being flattened into a generic sentence. `{name}` is the style's full name.
    genericDescription:
      "Estilo de cita para {name}. Del repositorio de Citation Style Language.",
    genericWhenUsed:
      "Al enviar trabajos a publicaciones que usan el estilo {name}, o al citar en ellas.",

    // The 26 discipline tokens `style-examples-generated.json` uses. Each English
    // value is the exact string the page printed before these keys existed — the raw
    // token with hyphens turned into spaces — so `generic-base` reads "generic base"
    // and `political_science` keeps its underscore. An unknown token (a new one from
    // upstream CSL) falls back to that same prettified form rather than to a key name.
    disciplineAnthropology: "antropología",
    disciplineAstronomy: "astronomía",
    disciplineBiology: "biología",
    disciplineBotany: "botánica",
    disciplineChemistry: "química",
    disciplineCommunications: "comunicación",
    disciplineEngineering: "ingeniería",
    disciplineGenericBase: "general",
    disciplineGeography: "geografía",
    disciplineGeology: "geología",
    disciplineHistory: "historia",
    disciplineHumanities: "humanidades",
    disciplineLaw: "derecho",
    disciplineLinguistics: "lingüística",
    disciplineLiterature: "literatura",
    disciplineMath: "matemáticas",
    disciplineMedicine: "medicina",
    disciplinePhilosophy: "filosofía",
    disciplinePhysics: "física",
    disciplinePoliticalScience: "ciencias políticas",
    disciplinePsychology: "psicología",
    disciplineScience: "ciencia",
    disciplineSocialScience: "ciencias sociales",
    disciplineSociology: "sociología",
    disciplineTheology: "teología",
    disciplineZoology: "zoología",

    // Plain-language descriptions of the citation-format classes the CSL definitions
    // declare. `{format}` in the fallback is the raw class name from the data.
    formatClassAuthorDate:
      "un estilo autor-fecha: las citas en el texto incluyen el autor y el año, y la lista de referencias se ordena alfabéticamente por autor",
    formatClassNumeric:
      "un estilo numérico: las fuentes se numeran en el orden en que se citan por primera vez, y las citas en el texto usan esos números",
    formatClassNote:
      "un estilo basado en notas: las citas aparecen en notas al pie o notas finales en lugar de entre paréntesis en el texto",
    formatClassLabel:
      "un estilo de etiqueta: las citas en el texto usan una etiqueta compacta formada por el nombre del autor y el año",
    formatClassAuthor:
      "un estilo solo-autor: las citas en el texto mencionan al autor, con los detalles completos en la lista de referencias",
    formatClassOther: "un estilo {format}",
    breadcrumbStyles: "Estilos de cita",
    internationalLabel: "Internacional",
    heading: "Generador de citas {style}",
    intro:
      "Crea citas {style} precisas para libros, artículos y páginas web con un clic.",
    examplesHeading: "Ejemplos de citas en {style}",
    examplesIntro: "Las mismas tres fuentes con el formato {style}:",
    examplesIntroWithFormat:
      "Las mismas tres fuentes con el formato {style} — {format}:",
    examplesNoteOnlyIntro:
      "{style} es un estilo basado en notas: no tiene lista de referencias y cada cita es una nota al pie. Así se cita nuestro artículo de revista de ejemplo en {style}:",
    labelJournalArticle: "Artículo de revista",
    labelBook: "Libro",
    labelWebsite: "Página web",
    labelFootnote: "Nota al pie (artículo de revista)",
    labelInText: "Cita en el texto (del artículo de revista)",
    aboutHeading: "Sobre {style}",
    whenToUseLabel: "Cuándo usar {style}:",
    commonInLabel: "Habitual en:",
    subjectAreasLabel: "Áreas temáticas:",
    formattingRulesLabel: "Reglas de formato:",
    parentStylePrefix: "{style} sigue el formato de",
    parentStyleSuffix: ", el estilo principal en el que se basa.",
    howToHeading: "Cómo usar este generador de {style}",
    howToBody:
      'Pega un DOI, la URL de un artículo de noticias o página web, o un BibTeX sin procesar en el cuadro de arriba. También puedes buscar un libro por título o autor y citarlo por su ISBN. Elige "{style}" en el menú desplegable de estilos (ya está preseleccionado en esta página) y pulsa "Generar cita." Copia el resultado en tu lista de referencias o bibliografía. Puedes editar la entrada generada (por ejemplo, corregir un título o añadir una fecha) antes de copiarla.',
    faqHeading: "Preguntas frecuentes sobre {style}",
    faqWhatIsQ: "¿Qué es {style}?",
    faqWhenQ: "¿Cuándo debo usar {style}?",
    faqInTextQ: "¿Cómo es una cita en el texto en {style}?",
    faqInTextA:
      "{style} es {format}. Citar nuestro artículo de revista de ejemplo en el texto se ve así: {example}",
    faqReferenceQ:
      "¿Cómo es una entrada de la lista de referencias en {style}?",
    faqReferenceA:
      "Un artículo de revista con el formato {style} se ve así: {example}",
    faqWhereQ: "¿Dónde se usa habitualmente {style}?",
    faqWhereA: "{style} se usa {region}. {whenUsed}",
    faqRegionDefault: "a nivel internacional",
    faqGenerateQ: "¿Cómo puedo generar una cita en {style}?",
    faqGenerateA:
      "Usa el generador gratuito de esta página: pega un DOI, una URL (por ejemplo, de una noticia) o BibTeX, o busca un libro por título o autor. Selecciona {style} como estilo de cita y pulsa Generar para obtener una referencia con formato que puedas copiar en tu trabajo o bibliografía.",
    footerSupports:
      "admite numerosos estilos de cita de todo el mundo. Nuestro generador usa estilos de Citation Style Language (CSL) del",
    footerRepoLink: "repositorio oficial de CSL",
  },

  // The source × style pages at `/<locale>/how-to-cite/<slug>` — 1,290 combinations.
  //
  // Same division as above: the citation templates, the worked examples generated from
  // each style's own CSL definition, the in-text samples and the hand-checked
  // formatting rules stay in the page module and in
  // `src/data/how-to-cite-examples-generated.json`. They are the citations themselves,
  // not prose about them. Source names, style names and editions come from
  // `src/data/how-to-cite.ts` and arrive here as placeholder values.
  crossLinks: {
    generateHeading: "Genera esta cita",
    generateBody:
      "Pega una URL, DOI, ISBN o los datos de la fuente y obtén la cita de {source} en {style}, lista para usar.",
    generateCta: "Abrir el {tool} →",
    guideHeading: "Profundiza",
    guideCta: "Lee la guía completa: {guide}",
    howToCiteHeading: "Cómo citar {source} en cada estilo",
    howToCiteBody:
      "Reglas de formato, plantillas y ejemplos resueltos para los estilos más solicitados.",
    howToCiteMore: "Todos los tipos de fuente y estilos →",
    citeNowHeading: "Cita {source} en {style} ahora",
    citeNowHowTo: "Reglas de formato y ejemplos resueltos",
    citeNowTool: "Genérala con el {tool}",
    citeNowHub: "Generador de citas {style}",
    hubSourcesHeading: "Cómo citar las fuentes más comunes en {style}",
    hubGuidesHeading: "Guías de {style}",
    hubCompareHeading: "Compara {style} con otros estilos",
    moreInCollection: "Más en {collection}",
    seeAllInCollection: "Ver todo en {collection}",
  },

  howToCitePage: {
    notFoundTitle: "No encontrado",
    title: "Cómo citar {source} en {style} ({edition})",
    metaTitle:
      "Cómo citar {source} en {style} [{edition}, actualizado en 2026]",
    metaDescription:
      "Aprende a citar {description} en formato {style} ({edition}). Ejemplos gratuitos, plantillas y guías de citas en el texto.",
    keywordCite: "citar {source} en {style}",
    keywordCitation: "cita de {source} en {style}",
    keywordHowTo: "cómo citar {source}",
    keywordExample: "ejemplo de cita en {style}",
    keywordReference: "referencia de {source} en {style}",
    // What each style calls the list at the back of the paper, in the two casings the
    // prose needs. MLA says "Works Cited", Chicago and the legal styles "Bibliography".
    refListWorksCited: "Lista de obras citadas",
    refListWorksCitedPage: "lista de obras citadas",
    refListBibliography: "Bibliografía",
    refListBibliographyLower: "bibliografía",
    refListReferenceList: "Referencias",
    refListReferenceListLower: "lista de referencias",
    backLink: "← Todas las guías de citación",
    lead: "Aprende a citar {description} en formato {style} ({edition}). Esta guía incluye el formato de la lista de referencias, ejemplos de citas en el texto y las normas clave de formato.",
    referenceFormatHeading: "Formato de {list} en {style}",
    referenceFormatIntro:
      "Este es el formato estándar de {style} para citar {source} en la sección {list}:",
    noTemplateNote:
      "Sigue la plantilla estándar de {style} para este tipo de fuente.",
    workedHeading: "Ejemplo de cita de {source} en {style}",
    workedIntro:
      "Una referencia completa en {style} para {source}, generada con la definición oficial del estilo CSL de {style} — el mismo motor que impulsa el generador de este sitio:",
    workedNoteOnlyIntro:
      "{style} no incluye {source} en la sección {list}: solo se cita en una nota. Aquí tienes esa nota, generada con la definición oficial del estilo CSL de {style}:",
    workedOscolaNote:
      "La salida de OSCOLA generada en este sitio sigue el estilo CSL de la 4.ª edición, porque todavía no se ha publicado un estilo CSL de la 5.ª edición en el repositorio de Citation Style Language. Ajústala a mano: sustituye cada ibid por el nombre abreviado y (n X), usa los identificadores de tribunal de la 5.ª edición, pon la forma abreviada de una ley entre comillas simples y prefiere un enlace permanente o un DOI a la fecha de acceso",
    workedCheckedIntro:
      "Una referencia completa en {style} para {source}, redactada a mano y verificada con las directrices oficiales de {style} para este tipo de fuente:",
    workedCheckedNoteOnlyIntro:
      "{style} no incluye {source} en la sección {list}: solo se cita en una nota. Aquí tienes esa nota, redactada a mano y verificada con las directrices oficiales de {style}:",
    workedInTextLabel: "Cita en el texto correspondiente:",
    inTextHeading: "Cita en el texto en {style}",
    inTextIntro:
      "Usa uno de estos formatos cuando menciones esta fuente dentro de tu trabajo:",
    inTextParenthetical: "Parentética",
    inTextNarrative: "Narrativa",
    rulesHeading: "Normas clave de formato de {style}",
    stepsHeading: "Guía paso a paso",
    step1Label: "Reúne la información de la fuente:",
    step1Body:
      "Recopila el nombre del autor, el título, la fecha de publicación y la URL o el DOI de tu {source}.",
    step2Label: "Da formato a la referencia:",
    step2Body:
      "Ordena los elementos siguiendo la plantilla de {style} ({edition}) que se muestra arriba.",
    step3Label: "Crea la cita en el texto:",
    step3Body:
      "Añade una cita parentética o narrativa en tu trabajo donde menciones esta fuente.",
    step4Label: "Añádela a la sección {list}:",
    step4Body: "Incluye la cita completa con formato al final de tu trabajo.",
    step5Label: "Revisa el formato:",
    step5Body:
      "Comprueba que la cursiva, la puntuación, las mayúsculas y las sangrías francesas cumplan los requisitos de {style}.",
    ctaHeading: "Genera esta cita automáticamente",
    ctaBody:
      "Olvídate del formato manual. Pega un DOI, una URL o un ISBN en CitationEasy y obtén una cita en {style} perfectamente formateada en segundos.",
    ctaButton: "Cita en {style} ahora →",
    otherStylesHeading: "Cita {source} en otros estilos",
    comboLink: "{source} en {style}",
    otherSourcesHeading: "Cita otras fuentes en {style}",
    resourcesHeading: "Más recursos",
    resourceGenerator: "Generador de citas gratuito ({count} estilos)",
    resourceTools: "Herramientas de escritura gratuitas",
    resourceCompare: "Compara estilos de cita",
    resourceGuides: "Todas las guías de citación",
    resourceCheatSheets: "Hojas de referencia rápida (APA, MLA, Chicago)",
    resourceGlossary:
      "Glosario de citación (et al., ibid., DOI, sangría francesa)",
    // HowTo structured data. Deliberately worded differently from the visible steps
    // above: the schema describes the task generically, the page walks the reader
    // through this particular source and style.
    schemaStep1Name: "Reunir la información de la fuente",
    schemaStep1Text:
      "Reúne toda la información necesaria de tu {source}: autor, título, fecha y URL o DOI.",
    schemaStep2Name: "Dar formato en {style}",
    schemaStep2Text:
      "Sigue las normas de formato de {style} ({edition}) para ordenar los elementos de la cita correctamente.",
    schemaStep3Name: "Crear la cita en el texto",
    schemaStep3Text:
      "Añade la cita correspondiente en el texto de tu trabajo usando el formato {style}.",
    schemaStep4Name: "Añadir a la lista de referencias",
    schemaStep4Text:
      "Incluye la cita completa en la sección {list} al final del trabajo.",
    faqHowQ: "¿Cómo se cita {source} en {style}?",
    faqHowA:
      "Para citar {source} en {style} ({edition}), incluye el autor, el título, la fecha y la ubicación de la fuente. Usa nuestro generador de citas gratuito para el formato automático.",
    faqFormatQ: "¿Cuál es el formato {style} para citar {source}?",
    faqFormatA: "El formato {style} para {source} es: {example}",
    faqFormatFallback:
      "El formato {style} exige elementos concretos, como el autor, el título, la fecha y la ubicación de la fuente, ordenados de una manera determinada.",
    faqInTextQ: "¿Cómo es la cita en el texto de {source} en {style}?",
    faqInTextA:
      "En {style}, la cita en el texto del ejemplo anterior es {example}",
  },

  // The 43 source types the how-to-cite pages are built from (`SOURCE_TYPES` in
  // `src/data/how-to-cite.ts`), in the five forms those pages print. Each form is a whole
  // phrase, never an article and a noun joined in code: an article agrees with its noun
  // in gender, number and case, elides in French and Italian, and has no counterpart
  // at all in Polish, Russian or Japanese.
  //
  // - `…Name` ("Book") is the type on its own, as a label: `howToCitePage.comboLink`.
  //   English also lower-cases it into the `keyword*` templates.
  // - `…Title` ("a Book") is the type as the object of "cite" in a title or heading:
  //   `title`, `metaTitle`, `workedHeading` and `otherStylesHeading`, plus
  //   `crossLinks.generateBody`, `howToCiteHeading` and `citeNowHeading`. Through
  //   `title` it is also the HowTo name, the OG card and every link to the page.
  // - `…Phrase` ("a book") is the same inside a sentence: `referenceFormatIntro`,
  //   `workedIntro`, `workedNoteOnlyIntro` and the five `faq*` questions and answers.
  // - `…Noun` ("book") is the bare noun inside a sentence, where English puts "your" in
  //   front of it: `step1Body` and `schemaStep1Text`. A translation also uses it in the
  //   `keyword*` templates, which read as search queries.
  // - `…Description` is what the type covers: `{description}` in `lead` and
  //   `metaDescription`.
  //
  // A language with case picks one case per form and words every template above around
  // it — German writes Title and Phrase in the accusative, Polish and Russian write Name
  // and Noun in the nominative. Changing a form's case means re-reading every template
  // that receives it. A key missing here falls back to the English data
  // (`howToCiteSourceForms`), so a new source type renders in English, not as a key.
  // Spanish: Title/Phrase carry their article ("un libro", "la Biblia"); Name/Noun are bare.
  howToCiteSources: {
    websiteName: "Página web",
    websiteTitle: "una página web",
    websitePhrase: "una página web",
    websiteNoun: "página web",
    websiteDescription: "páginas web, artículos en línea y contenido web",

    bookName: "Libro",
    bookTitle: "un libro",
    bookPhrase: "un libro",
    bookNoun: "libro",
    bookDescription: "libros impresos y electrónicos",

    journalArticleName: "Artículo de revista",
    journalArticleTitle: "un artículo de revista",
    journalArticlePhrase: "un artículo de revista",
    journalArticleNoun: "artículo de revista",
    journalArticleDescription:
      "artículos de revistas académicas y revisados por pares",

    youtubeVideoName: "Video de YouTube",
    youtubeVideoTitle: "un video de YouTube",
    youtubeVideoPhrase: "un video de YouTube",
    youtubeVideoNoun: "video de YouTube",
    youtubeVideoDescription: "videos de YouTube y otros videos en línea",

    podcastName: "Pódcast",
    podcastTitle: "un pódcast",
    podcastPhrase: "un pódcast",
    podcastNoun: "pódcast",
    podcastDescription: "episodios y series de pódcast",

    newspaperArticleName: "Artículo de periódico",
    newspaperArticleTitle: "un artículo de periódico",
    newspaperArticlePhrase: "un artículo de periódico",
    newspaperArticleNoun: "artículo de periódico",
    newspaperArticleDescription: "artículos de periódico impresos y en línea",

    magazineArticleName: "Artículo de revista de divulgación",
    magazineArticleTitle: "un artículo de revista de divulgación",
    magazineArticlePhrase: "un artículo de revista de divulgación",
    magazineArticleNoun: "artículo de revista de divulgación",
    magazineArticleDescription:
      "artículos de revistas de divulgación impresas y en línea",

    blogPostName: "Entrada de blog",
    blogPostTitle: "una entrada de blog",
    blogPostPhrase: "una entrada de blog",
    blogPostNoun: "entrada de blog",
    blogPostDescription: "entradas de blog y artículos de opinión en línea",

    wikipediaName: "Wikipedia",
    wikipediaTitle: "Wikipedia",
    wikipediaPhrase: "Wikipedia",
    wikipediaNoun: "artículo de Wikipedia",
    wikipediaDescription: "artículos de Wikipedia y entradas de wikis",

    pdfName: "PDF",
    pdfTitle: "un PDF",
    pdfPhrase: "un PDF",
    pdfNoun: "PDF",
    pdfDescription: "documentos e informes en PDF",

    interviewName: "Entrevista",
    interviewTitle: "una entrevista",
    interviewPhrase: "una entrevista",
    interviewNoun: "entrevista",
    interviewDescription:
      "entrevistas personales, publicadas y emitidas por radio o televisión",

    socialMediaName: "Publicación en redes sociales",
    socialMediaTitle: "una publicación en redes sociales",
    socialMediaPhrase: "una publicación en redes sociales",
    socialMediaNoun: "publicación en redes sociales",
    socialMediaDescription:
      "tuits, publicaciones de Instagram y Facebook, y videos de TikTok",

    governmentDocumentName: "Documento gubernamental",
    governmentDocumentTitle: "un documento gubernamental",
    governmentDocumentPhrase: "un documento gubernamental",
    governmentDocumentNoun: "documento gubernamental",
    governmentDocumentDescription:
      "informes gubernamentales, legislación y documentos oficiales",

    dissertationName: "Tesis doctoral o de maestría",
    dissertationTitle: "una tesis doctoral o de maestría",
    dissertationPhrase: "una tesis doctoral o de maestría",
    dissertationNoun: "tesis",
    dissertationDescription: "tesis doctorales y tesis de maestría",

    conferencePaperName: "Ponencia de congreso",
    conferencePaperTitle: "una ponencia de congreso",
    conferencePaperPhrase: "una ponencia de congreso",
    conferencePaperNoun: "ponencia de congreso",
    conferencePaperDescription: "actas y ponencias de congresos",

    lectureNotesName: "Clase o PowerPoint",
    lectureNotesTitle: "una clase o un PowerPoint",
    lectureNotesPhrase: "una clase o un PowerPoint",
    lectureNotesNoun: "clase o PowerPoint",
    lectureNotesDescription: "clases, diapositivas y materiales de curso",

    webinarName: "Seminario web",
    webinarTitle: "un seminario web",
    webinarPhrase: "un seminario web",
    webinarNoun: "seminario web",
    webinarDescription: "seminarios web en directo y grabados",

    onlineCourseName: "Curso en línea",
    onlineCourseTitle: "un curso en línea",
    onlineCoursePhrase: "un curso en línea",
    onlineCourseNoun: "curso en línea",
    onlineCourseDescription:
      "cursos en línea y MOOC (Coursera, edX, Udemy, FutureLearn)",

    presentationSlidesName: "Presentación de PowerPoint",
    presentationSlidesTitle: "una presentación de PowerPoint",
    presentationSlidesPhrase: "una presentación de PowerPoint",
    presentationSlidesNoun: "presentación de PowerPoint",
    presentationSlidesDescription:
      "diapositivas de PowerPoint y de otras presentaciones",

    imageName: "Imagen o fotografía",
    imageTitle: "una imagen o fotografía",
    imagePhrase: "una imagen o fotografía",
    imageNoun: "imagen o fotografía",
    imageDescription:
      "fotografías, obras de arte, gráficos e imágenes en línea",

    movieName: "Película",
    movieTitle: "una película",
    moviePhrase: "una película",
    movieNoun: "película",
    movieDescription: "una película",

    documentaryName: "Documental",
    documentaryTitle: "un documental",
    documentaryPhrase: "un documental",
    documentaryNoun: "documental",
    documentaryDescription: "documentales y películas de no ficción",

    tvShowName: "Programa de televisión",
    tvShowTitle: "un programa de televisión",
    tvShowPhrase: "un programa de televisión",
    tvShowNoun: "programa de televisión",
    tvShowDescription: "un programa o una serie de televisión",

    songName: "Canción o pieza musical",
    songTitle: "una canción o pieza musical",
    songPhrase: "una canción o pieza musical",
    songNoun: "canción",
    songDescription: "una canción o una grabación musical",

    poemName: "Poema",
    poemTitle: "un poema",
    poemPhrase: "un poema",
    poemNoun: "poema",
    poemDescription: "poemas, versos y poesía de antologías o recopilaciones",

    courtCaseName: "Caso judicial",
    courtCaseTitle: "un caso judicial",
    courtCasePhrase: "un caso judicial",
    courtCaseNoun: "caso judicial",
    courtCaseDescription: "un caso judicial o una sentencia",

    patentName: "Patente",
    patentTitle: "una patente",
    patentPhrase: "una patente",
    patentNoun: "patente",
    patentDescription: "una patente o una solicitud de propiedad intelectual",

    encyclopediaName: "Entrada de enciclopedia",
    encyclopediaTitle: "una entrada de enciclopedia",
    encyclopediaPhrase: "una entrada de enciclopedia",
    encyclopediaNoun: "entrada de enciclopedia",
    encyclopediaDescription: "un artículo o una entrada de enciclopedia",

    dictionaryName: "Entrada de diccionario",
    dictionaryTitle: "una entrada de diccionario",
    dictionaryPhrase: "una entrada de diccionario",
    dictionaryNoun: "entrada de diccionario",
    dictionaryDescription: "una definición o una entrada de diccionario",

    datasetName: "Conjunto de datos",
    datasetTitle: "un conjunto de datos",
    datasetPhrase: "un conjunto de datos",
    datasetNoun: "conjunto de datos",
    datasetDescription: "un conjunto de datos o una base de datos",

    softwareName: "Software o aplicación",
    softwareTitle: "software o una aplicación",
    softwarePhrase: "software o una aplicación",
    softwareNoun: "software o aplicación",
    softwareDescription: "software, una aplicación o un programa informático",

    videoGameName: "Videojuego",
    videoGameTitle: "un videojuego",
    videoGamePhrase: "un videojuego",
    videoGameNoun: "videojuego",
    videoGameDescription: "videojuegos y software de juegos interactivos",

    pressReleaseName: "Comunicado de prensa",
    pressReleaseTitle: "un comunicado de prensa",
    pressReleasePhrase: "un comunicado de prensa",
    pressReleaseNoun: "comunicado de prensa",
    pressReleaseDescription: "un comunicado o una nota de prensa",

    reportName: "Informe",
    reportTitle: "un informe",
    reportPhrase: "un informe",
    reportNoun: "informe",
    reportDescription: "un informe o un libro blanco",

    preprintName: "Preprint",
    preprintTitle: "un preprint",
    preprintPhrase: "un preprint",
    preprintNoun: "preprint",
    preprintDescription:
      "artículos de arXiv, bioRxiv y otros servidores de preprints",

    bookChapterName: "Capítulo de libro",
    bookChapterTitle: "un capítulo de libro",
    bookChapterPhrase: "un capítulo de libro",
    bookChapterNoun: "capítulo de libro",
    bookChapterDescription: "capítulos y ensayos de obras colectivas",

    chatgptName: "ChatGPT",
    chatgptTitle: "ChatGPT",
    chatgptPhrase: "ChatGPT",
    chatgptNoun: "conversación con ChatGPT",
    chatgptDescription: "ChatGPT y otras herramientas de IA generativa",

    bibleName: "Biblia",
    bibleTitle: "la Biblia",
    biblePhrase: "la Biblia",
    bibleNoun: "Biblia",
    bibleDescription: "la Biblia, las escrituras y otros textos sagrados",

    tedTalkName: "Charla TED",
    tedTalkTitle: "una charla TED",
    tedTalkPhrase: "una charla TED",
    tedTalkNoun: "charla TED",
    tedTalkDescription: "charlas TED y otras conferencias grabadas",

    emailName: "Correo electrónico",
    emailTitle: "un correo electrónico",
    emailPhrase: "un correo electrónico",
    emailNoun: "correo electrónico",
    emailDescription:
      "correos electrónicos, cartas y comunicaciones personales",

    redditName: "Publicación de Reddit o de un foro",
    redditTitle: "una publicación de Reddit o de un foro",
    redditPhrase: "una publicación de Reddit",
    redditNoun: "publicación de Reddit",
    redditDescription:
      "hilos de Reddit, AMA y mensajes de foros en línea o tablones de discusión",

    tweetName: "Publicación de X (Twitter)",
    tweetTitle: "una publicación de X (Twitter)",
    tweetPhrase: "una publicación de X",
    tweetNoun: "publicación de X",
    tweetDescription: "tuits y publicaciones en X (antes Twitter)",

    mapName: "Mapa",
    mapTitle: "un mapa",
    mapPhrase: "un mapa",
    mapNoun: "mapa",
    mapDescription: "mapas impresos, atlas y mapas dinámicos como Google Maps",

    claudeName: "Claude",
    claudeTitle: "Claude",
    claudePhrase: "Claude",
    claudeNoun: "conversación con Claude",
    claudeDescription:
      "conversaciones con Claude y otros contenidos generados por la IA de Anthropic",

    geminiName: "Gemini",
    geminiTitle: "Gemini",
    geminiPhrase: "Gemini",
    geminiNoun: "conversación con Gemini",
    geminiDescription:
      "conversaciones con Google Gemini y contenidos generados por IA",

    copilotName: "Microsoft Copilot",
    copilotTitle: "Microsoft Copilot",
    copilotPhrase: "Microsoft Copilot",
    copilotNoun: "conversación con Microsoft Copilot",
    copilotDescription:
      "conversaciones y respuestas de IA de Microsoft Copilot",

    perplexityName: "Perplexity",
    perplexityTitle: "Perplexity",
    perplexityPhrase: "Perplexity",
    perplexityNoun: "conversación con Perplexity",
    perplexityDescription:
      "respuestas e hilos de Perplexity y las fuentes que citan",

    aiImageName: "Imagen generada por IA",
    aiImageTitle: "una imagen generada por IA",
    aiImagePhrase: "una imagen generada por IA",
    aiImageNoun: "imagen generada por IA",
    aiImageDescription:
      "imágenes creadas con DALL·E, Midjourney, Gemini y otras herramientas de IA",

    tiktokName: "Video de TikTok",
    tiktokTitle: "un video de TikTok",
    tiktokPhrase: "un video de TikTok",
    tiktokNoun: "video de TikTok",
    tiktokDescription: "videos de TikTok y publicaciones de creadores",

    instagramName: "Publicación de Instagram",
    instagramTitle: "una publicación de Instagram",
    instagramPhrase: "una publicación de Instagram",
    instagramNoun: "publicación de Instagram",
    instagramDescription: "publicaciones, reels e historias de Instagram",

    statuteName: "Ley o norma jurídica",
    statuteTitle: "una ley o norma jurídica",
    statutePhrase: "una ley o norma jurídica",
    statuteNoun: "ley o norma jurídica",
    statuteDescription:
      "leyes aprobadas por el poder legislativo, normas jurídicas y códigos",

    isoStandardName: "Norma ISO",
    isoStandardTitle: "una norma ISO",
    isoStandardPhrase: "una norma ISO",
    isoStandardNoun: "norma ISO",
    isoStandardDescription:
      "normas ISO, IEC y otras normas técnicas publicadas",

    quranName: "Corán",
    quranTitle: "un pasaje del Corán",
    quranPhrase: "un pasaje del Corán",
    quranNoun: "pasaje del Corán",
    quranDescription: "el Corán, en árabe o en traducción",

    ebookNoPageNumbersName: "Libro electrónico sin números de página",
    ebookNoPageNumbersTitle: "un libro electrónico sin números de página",
    ebookNoPageNumbersPhrase: "un libro electrónico sin números de página",
    ebookNoPageNumbersNoun: "libro electrónico sin números de página",
    ebookNoPageNumbersDescription:
      "libros electrónicos de Kindle, EPUB y en línea sin paginación fija",

    speechName: "Discurso",
    speechTitle: "un discurso",
    speechPhrase: "un discurso",
    speechNoun: "discurso",
    speechDescription:
      "discursos, alocuciones y sermones, en directo o grabados",

    letterName: "Carta",
    letterTitle: "una carta",
    letterPhrase: "una carta",
    letterNoun: "carta",
    letterDescription: "cartas publicadas, cartas de archivo y cartas abiertas",

    secondarySourceName: "Fuente secundaria",
    secondarySourceTitle: "una fuente secundaria",
    secondarySourcePhrase: "una fuente secundaria",
    secondarySourceNoun: "fuente secundaria",
    secondarySourceDescription: "una fuente que aparece citada en otra obra",
  },

  // ---------------------------------------------------------------------------
  // NOT YET TRANSLATED. These five sections were copied verbatim from `en.ts` so
  // this file still satisfies `Dictionary`; the page types they cover
  // (`taxonomy`, `compare`, `convert`) are deliberately absent from
  // TRANSLATED_PAGE_TYPES, so nothing publishes them in this language yet. The
  // translation pass replaces the strings and only then adds the types.
  // ---------------------------------------------------------------------------
  // The three browse-by-facet pages under `/<locale>/citation-styles/…` — 32 academic
  // fields, 68 countries and 39 subject categories, 139 pages in one section because
  // they share a shell, a breadcrumb trail and a styles browser.
  //
  // The style NAMES, slugs and the CSL catalog itself stay in `src/data/**`: those are
  // subject matter. What the facets themselves are called is not — "Political Science",
  // "United Kingdom" and "Film & Media" are ordinary nouns that every reader expects in
  // their own language, and there are only 139 of them in total (see the `academicFields`,
  // `regions` and `styleCategories` sections below).
  taxonomyPage: {
    // ---- /citation-styles/field/<slug> ----
    fieldNotFoundTitle: "No encontrado",
    fieldMetaTitle:
      "Estilos de cita para {field} - Los mejores estilos de referencia [2026]",
    fieldMetaDescription:
      "Descubre los estilos de cita más utilizados en {field}. Encuentra el formato de referencia adecuado para tu trabajo, tesis o artículo de revista de {fieldLower}.",
    fieldOgTitle: "Estilos de cita para {field}",
    fieldOgSubtitle: "Habitualmente exigidos: {styles}.",
    fieldOgKind: "Por disciplina académica",
    fieldKeywordStyle: "estilo de cita de {field}",
    fieldKeywordReferencing: "referencias de {field}",
    fieldKeywordBest: "mejor estilo de cita para {fieldLower}",
    fieldKeywordBibliography: "formato de bibliografía de {fieldLower}",
    fieldKeywordDiscipline: "estilos de cita por disciplina",
    fieldBackLink: "← Todos los estilos de cita",
    fieldHeading: "Estilos de cita para {field}",
    fieldRecommendedHeading: "Estilos de cita recomendados",
    fieldRecommendedIntro:
      "Los siguientes estilos de cita se usan habitualmente en la investigación, las revistas y los trabajos de {field}. Selecciona un estilo para obtener más información y empezar a generar citas.",
    fieldStyleFallback:
      "Un estilo de cita utilizado en {field} y disciplinas afines.",
    fieldRelatedHeading: "Disciplinas relacionadas",
    fieldRelatedIntro:
      "Explora los estilos de cita utilizados en disciplinas académicas afines:",
    fieldCtaHeading: "Explora todos los estilos de cita",
    fieldCtaBody:
      "CitationEasy admite {count} estilos de cita en todas las disciplinas académicas. Explora la colección completa para encontrar el formato exacto que necesitas.",
    fieldCtaButton: "Explorar todos los estilos →",
    fieldFaqWhichQ: "¿Qué estilo de cita se usa en {field}?",
    fieldFaqWhichA:
      "El estilo de cita que se usa en {field} depende de la revista, la institución o los requisitos concretos del profesor. Entre los estilos habituales están {styles}. Consulta siempre las indicaciones de tu trabajo o la revista de destino para conocer el formato exigido.",
    fieldFaqSameQ: "¿Todas las revistas de {field} usan el mismo estilo?",
    fieldFaqSameA:
      "No, no todas las revistas de {field} usan el mismo estilo de cita. Aunque ciertos estilos son más comunes en el campo, cada revista puede tener sus propios requisitos de formato. Consulta siempre las normas de envío de la revista antes de preparar tu manuscrito.",
    fieldFaqChooseQ:
      "¿Cómo elijo un estilo de cita para mi trabajo de {field}?",
    fieldFaqChooseA:
      "Para elegir un estilo de cita para tu trabajo de {field}, comprueba primero si tu profesor o la revista de destino exige un estilo concreto. Si no es así, usa uno de los estilos recomendados para {field} que se indican arriba. CitationEasy admite más de {count} estilos y puede generar citas en cualquiera de ellos gratis.",

    // ---- /citation-styles/country/<slug> ----
    countryNotFoundTitle: "País no encontrado",
    countryMetaTitle: "Estilos de cita de {country} - APA, MLA y más",
    countryMetaDescription:
      "Explora los estilos de cita usados en {country}. Generador de citas gratuito para los formatos de {country}.",
    countryOgTitle: "Estilos de cita usados en {country}",
    countryOgKind: "Por país",
    countryHeading: "Estilos de cita de {country}",
    countryIntroOne:
      "{count} estilo de cita usado habitualmente en {country} o asociado con este país. Elígelo para abrir un generador dedicado.",
    countryIntroMany:
      "{count} estilos de cita usados habitualmente en {country} o asociados con este país. Elige uno para abrir un generador dedicado.",
    countryBrowserTitle: "Estilos de cita de {country}",

    // ---- /citation-styles/type/<slug> ----
    typeNotFoundTitle: "Tipo no encontrado",
    typeMetaTitle: "Estilos de cita de {type} - Generador gratuito",
    typeMetaDescription:
      "Estilos de cita para {typeLower}. APA, MLA, Vancouver y más. Generador de citas gratuito.",
    typeOgTitle: "Estilos de cita de {type}",
    typeOgKind: "Por materia",
    typeHeading: "Estilos de cita de {type}",
    typeIntroOne:
      "{count} estilo de cita para {typeLower}. Elígelo para abrir un generador dedicado con preguntas frecuentes.",
    typeIntroMany:
      "{count} estilos de cita para {typeLower}. Elige uno para abrir un generador dedicado con preguntas frecuentes.",
    typeBrowserTitle: "Estilos de cita de {type}",

    // ---- Shared chrome ----
    breadcrumbStyles: "Estilos de cita",
    // Title case, because the BreadcrumbList markup says "Citation Styles" while the
    // visible trail says "Citation styles". Two keys rather than one so translating
    // either does not silently change the other.
    breadcrumbStylesSchema: "Estilos de Cita",
    breadcrumbByCountry: "Por país",
    breadcrumbByDiscipline: "Por disciplina",
    allStylesLink: "← Todos los estilos de cita",
    internationalLabel: "Internacional",
  },

  // The 32 academic fields (`ACADEMIC_FIELDS` in src/data/style-pages.ts), keyed by
  // slug. Their one-sentence `description` is next door in `academicFieldDescriptions`.
  academicFields: {
    psychology: "Psicología",
    education: "Educación",
    sociology: "Sociología",
    anthropology: "Antropología",
    politicalScience: "Ciencias políticas",
    economics: "Economía",
    business: "Empresa y gestión",
    literature: "Literatura y lenguas",
    history: "Historia",
    philosophy: "Filosofía",
    law: "Derecho y estudios jurídicos",
    medicine: "Medicina y ciencias clínicas",
    nursing: "Enfermería y obstetricia",
    publicHealth: "Salud pública y epidemiología",
    pharmacy: "Farmacia",
    biology: "Biología y ciencias de la vida",
    chemistry: "Química",
    physics: "Física",
    engineering: "Ingeniería",
    computerScience: "Ciencias de la computación",
    mathematics: "Matemáticas y estadística",
    environmentalScience: "Ciencias ambientales",
    earthScience: "Ciencias de la Tierra y geología",
    artHistory: "Historia del arte y artes visuales",
    music: "Música y musicología",
    theology: "Teología y estudios religiosos",
    socialWork: "Trabajo social",
    linguistics: "Lingüística",
    architecture: "Arquitectura y urbanismo",
    filmMedia: "Cine y estudios de medios",
    journalism: "Periodismo y comunicación",
    criminology: "Criminología y justicia penal",
  },

  // The same 32 fields' one-sentence ledes — the opening paragraph of every
  // `/citation-styles/field/<slug>` page.
  //
  // Unlike everything above, these are hand-written per field rather than mail-merged
  // from a template, which is why the extraction pass left them in
  // `src/data/style-pages.ts`: translating them is a content job, not a string swap.
  // But "hand-written" describes how they were produced, not where they belong. With
  // the chrome translated and the lede still English, the page is the hybrid CLAUDE.md
  // calls worse than an honest redirect — translated furniture around an English core.
  // `academicFieldDescription()` (src/lib/i18n/data-labels.ts) falls back to the data
  // file's own value, so a field added upstream is untranslated rather than blank.
  academicFieldDescriptions: {
    psychology:
      "Estilos de cita utilizados habitualmente en psicología, ciencias del comportamiento e investigación cognitiva.",
    education:
      "Estilos de cita para la investigación educativa, la pedagogía y los estudios curriculares.",
    sociology:
      "Estilos de cita utilizados en la investigación sociológica y la teoría social.",
    anthropology:
      "Estilos de cita para la investigación antropológica y etnográfica.",
    politicalScience:
      "Estilos de cita para ciencias políticas, gobierno e investigación en políticas públicas.",
    economics:
      "Estilos de cita utilizados en investigación económica y econométrica.",
    business:
      "Estilos de cita para investigación en empresa, gestión, marketing y finanzas.",
    literature:
      "Estilos de cita para estudios literarios, lingüística e investigación sobre el lenguaje.",
    history:
      "Estilos de cita para investigación histórica, historiografía y estudios de archivo.",
    philosophy:
      "Estilos de cita para la escritura filosófica y la investigación en ética.",
    law: "Estilos de cita jurídica para documentos judiciales, revistas de derecho y producción académica jurídica.",
    medicine:
      "Estilos de cita para investigación médica, estudios clínicos y atención sanitaria.",
    nursing:
      "Estilos de cita para la investigación en enfermería y la práctica sanitaria.",
    publicHealth:
      "Estilos de cita para investigación en salud pública y estudios epidemiológicos.",
    pharmacy:
      "Estilos de cita para investigación farmacéutica y farmacológica.",
    biology:
      "Estilos de cita para investigación biológica, ecología y genética.",
    chemistry:
      "Estilos de cita para química, bioquímica e investigación en ciencia de los materiales.",
    physics:
      "Estilos de cita para física, astrofísica e investigación en física aplicada.",
    engineering:
      "Estilos de cita para ingeniería e investigación en tecnología aplicada.",
    computerScience:
      "Estilos de cita para ciencias de la computación, IA e investigación en ingeniería de software.",
    mathematics:
      "Estilos de cita para matemáticas, matemática aplicada e investigación estadística.",
    environmentalScience:
      "Estilos de cita para investigación ambiental y estudios de sostenibilidad.",
    earthScience:
      "Estilos de cita para geología, geofísica e investigación en ciencias de la Tierra.",
    artHistory:
      "Estilos de cita para historia del arte, cultura visual y estudios museísticos.",
    music:
      "Estilos de cita para investigación musical, teoría y estudios interpretativos.",
    theology:
      "Estilos de cita para la producción académica teológica y religiosa.",
    socialWork:
      "Estilos de cita para investigación y práctica en trabajo social.",
    linguistics:
      "Estilos de cita para lingüística, fonética y ciencia del lenguaje.",
    architecture:
      "Estilos de cita para arquitectura, diseño urbano e investigación en planificación.",
    filmMedia:
      "Estilos de cita para estudios de cine, televisión, medios y comunicación.",
    journalism:
      "Estilos de cita para periodismo, estudios de medios e investigación en comunicación.",
    criminology:
      "Estilos de cita para criminología e investigación en justicia penal.",
  },

  // The 68 country/region facets (`REGIONS` in src/data/citation-styles.ts), keyed by
  // slug. `getRegionCountryCode` still keys off the English data value, so translating these
  // moves the label without moving the flag.
  regions: {
    unitedStates: "Estados Unidos",
    unitedKingdom: "Reino Unido",
    australia: "Australia",
    canada: "Canadá",
    newZealand: "Nueva Zelanda",
    ireland: "Irlanda",
    southAfrica: "Sudáfrica",
    germany: "Alemania",
    france: "Francia",
    spain: "España",
    italy: "Italia",
    netherlands: "Países Bajos",
    belgium: "Bélgica",
    switzerland: "Suiza",
    austria: "Austria",
    portugal: "Portugal",
    luxembourg: "Luxemburgo",
    sweden: "Suecia",
    norway: "Noruega",
    denmark: "Dinamarca",
    finland: "Finlandia",
    iceland: "Islandia",
    poland: "Polonia",
    czechRepublic: "República Checa",
    hungary: "Hungría",
    romania: "Rumanía",
    russia: "Rusia",
    ukraine: "Ucrania",
    greece: "Grecia",
    turkey: "Turquía",
    croatia: "Croacia",
    slovenia: "Eslovenia",
    slovakia: "Eslovaquia",
    serbia: "Serbia",
    bulgaria: "Bulgaria",
    lithuania: "Lituania",
    latvia: "Letonia",
    estonia: "Estonia",
    japan: "Japón",
    china: "China",
    southKorea: "Corea del Sur",
    india: "India",
    singapore: "Singapur",
    hongKong: "Hong Kong",
    taiwan: "Taiwán",
    malaysia: "Malasia",
    indonesia: "Indonesia",
    thailand: "Tailandia",
    vietnam: "Vietnam",
    philippines: "Filipinas",
    pakistan: "Pakistán",
    bangladesh: "Bangladés",
    sriLanka: "Sri Lanka",
    israel: "Israel",
    saudiArabia: "Arabia Saudita",
    unitedArabEmirates: "Emiratos Árabes Unidos",
    brazil: "Brasil",
    mexico: "México",
    argentina: "Argentina",
    chile: "Chile",
    colombia: "Colombia",
    peru: "Perú",
    venezuela: "Venezuela",
    egypt: "Egipto",
    nigeria: "Nigeria",
    kenya: "Kenia",
    morocco: "Marruecos",
    international: "Internacional",
  },

  // The 39 subject categories (`CATEGORIES` in src/data/citation-styles.ts), keyed by
  // slug. The emoji beside each one stays in the data — it is language-neutral.
  styleCategories: {
    medicine: "Medicina",
    nursing: "Enfermería",
    publicHealth: "Salud pública",
    pharmacy: "Farmacia",
    dentistry: "Odontología",
    veterinary: "Ciencias veterinarias",
    psychology: "Psicología",
    sociology: "Sociología",
    anthropology: "Antropología",
    politicalScience: "Ciencias políticas",
    economics: "Economía",
    education: "Educación",
    socialWork: "Trabajo social",
    biology: "Biología",
    chemistry: "Química",
    physics: "Física",
    environmentalScience: "Ciencias ambientales",
    earthScience: "Ciencias de la Tierra y geología",
    agriculture: "Agricultura",
    ecology: "Ecología",
    engineering: "Ingeniería",
    computerScience: "Ciencias de la computación",
    mathematics: "Matemáticas",
    statistics: "Estadística",
    history: "Historia",
    literature: "Literatura",
    philosophy: "Filosofía",
    linguistics: "Lingüística",
    artHistory: "Historia del arte",
    music: "Música",
    filmMedia: "Cine y medios",
    archaeology: "Arqueología",
    law: "Derecho",
    business: "Empresa",
    architecture: "Arquitectura",
    religion: "Religión y teología",
    geography: "Geografía",
    general: "General / Multidisciplinario",
    other: "Otro",
  },

  // The closed vocabularies `STYLE_LANDING_PAGES` (src/data/style-pages.ts) draws on for
  // the 30 major styles: what each style calls its end-of-paper list, the shape of its
  // in-text citation, its edition, and the disciplines that require it. The comparison
  // pages print all four in a table and the field pages print two of them under every
  // style card, so leaving them in the data means a translated page with an English
  // table in the middle of it.
  //
  // Style NAMES (`name`, `fullName`) are NOT here: a style's official name is what it
  // is called everywhere. The 30 hand-written taglines are, in `styleTaglines` below.
  styleFacts: {
    // `referenceListName` — 6 distinct values across the 30 styles.
    listReferenceList: "Lista de referencias",
    listWorksCited: "Obras citadas",
    listReferences: "Referencias",
    listBibliography: "Bibliografía",
    listTableOfAuthorities: "Tabla de autoridades",
    listReferencesCited: "Referencias citadas",
    listLowerReferenceList: "lista de referencias",
    listLowerWorksCited: "obras citadas",
    listLowerReferences: "referencias",
    listLowerBibliography: "bibliografía",
    listLowerTableOfAuthorities: "tabla de autoridades",
    listLowerReferencesCited: "referencias citadas",

    // `inTextFormat` — 14 distinct values. "Author", "Year" and "Page" are slot names,
    // not part of the citation, so they read as English words on a Spanish page.
    inTextAuthorCommaYear: "(Autor, Año)",
    inTextAuthorPage: "(Autor Página)",
    inTextAuthorYear: "(Autor Año)",
    inTextSuperscriptOrBracketed:
      "Números en superíndice o entre corchetes (1)",
    inTextBracketedNumbers: "Números entre corchetes [1]",
    inTextSuperscriptNumbers: "Números en superíndice¹",
    inTextSuperscriptOrAuthorYear: "Números en superíndice o (Autor Año)",
    inTextKeyBased: "Referencias basadas en claves",
    inTextSuperscriptOrAuthorCommaYear: "Números en superíndice o (Autor, Año)",
    inTextFootnotesCaseLegislation:
      "Notas al pie con citas de jurisprudencia y legislación",
    inTextFootnotesSignals: "Notas al pie con abreviaturas de señal",
    inTextFootnotesPinpoint: "Notas al pie con referencias precisas",
    inTextParenNumbers: "Números entre paréntesis (1)",
    inTextItalicNumbers: "Números en cursiva (1)",

    // `edition` — 12 distinct values. The ordinal and the word "edition" translate; the
    // handbook name inside "Cite Them Right 11th edition" does not.
    edition4th: "4.ª edición",
    edition5th: "5.ª edición",
    edition7th: "7.ª edición",
    edition8th: "8.ª edición",
    edition9th: "9.ª edición",
    edition11th: "11.ª edición",
    edition18th: "18.ª edición",
    edition22nd: "22.ª edición",
    editionCiteThemRight11th: "Cite Them Right, 11.ª edición",
    editionIcmjeStandard: "estándar ICMJE",
    editionStandard: "estándar",
    editionCurrent: "actual",

    // `fields` — the 67 discipline names the 30 styles are tagged with.
    fieldAcoustics: "acústica",
    fieldAnthropology: "antropología",
    fieldAppliedPhysics: "física aplicada",
    fieldArchaeology: "arqueología",
    fieldArts: "artes",
    fieldAstrophysics: "astrofísica",
    fieldAustralianLaw: "derecho australiano",
    fieldBiochemistry: "bioquímica",
    fieldBiology: "biología",
    fieldBiomedicalSciences: "ciencias biomédicas",
    fieldBiotechnology: "biotecnología",
    fieldBusiness: "empresa",
    fieldCellBiology: "biología celular",
    fieldChemicalEngineering: "ingeniería química",
    fieldChemistry: "química",
    fieldClinicalResearch: "investigación clínica",
    fieldComputerScience: "ciencias de la computación",
    fieldCondensedMatter: "materia condensada",
    fieldCourtDocuments: "documentos judiciales",
    fieldCriminology: "criminología",
    fieldCulturalStudies: "estudios culturales",
    fieldDemography: "demografía",
    fieldEcology: "ecología",
    fieldEducation: "educación",
    fieldElectronics: "electrónica",
    fieldEngineering: "ingeniería",
    fieldEnvironmentalScience: "ciencias ambientales",
    fieldEpidemiology: "epidemiología",
    fieldEthnography: "etnografía",
    fieldGenetics: "genética",
    fieldGeology: "geología",
    fieldGlobalHealth: "salud global",
    fieldHealthSciences: "ciencias de la salud",
    fieldHistory: "historia",
    fieldHumanities: "humanidades",
    fieldInformationSystems: "sistemas de información",
    fieldInformationTechnology: "tecnología de la información",
    fieldInternationalRelations: "relaciones internacionales",
    fieldJurisprudence: "jurisprudencia",
    fieldLanguages: "lenguas",
    fieldLaw: "derecho",
    fieldLawReview: "revista jurídica",
    fieldLegalStudies: "estudios jurídicos",
    fieldLifeSciences: "ciencias de la vida",
    fieldLiterature: "literatura",
    fieldMaterialsScience: "ciencia de los materiales",
    fieldMathematics: "matemáticas",
    fieldMedicine: "medicina",
    fieldMolecularBiology: "biología molecular",
    fieldMultidisciplinary: "multidisciplinar",
    fieldMultidisciplinaryScience: "ciencia multidisciplinar",
    fieldNaturalSciences: "ciencias naturales",
    fieldNursing: "enfermería",
    fieldPharmacy: "farmacia",
    fieldPhysics: "física",
    fieldPoliticalScience: "ciencias políticas",
    fieldPsychology: "psicología",
    fieldPublicHealth: "salud pública",
    fieldPublicPolicy: "política pública",
    fieldPublishing: "publicación",
    fieldScience: "ciencia",
    fieldSciences: "ciencias",
    fieldSocialSciences: "ciencias sociales",
    fieldSocialWork: "trabajo social",
    fieldSociology: "sociología",
    fieldSoftwareEngineering: "ingeniería de software",
    fieldTheology: "teología",
  },

  // The 30 major styles' hero taglines (`STYLE_LANDING_PAGES[].tagline`), keyed by
  // slug.
  //
  // Per-page hand-written copy, not a template — which is why they outlived the
  // extraction pass in `src/data/style-pages.ts`. Three page types print them: the
  // style landing page's hero, the card for each recommended style on a field
  // taxonomy page, and the lower-cased clause inside a comparison page's opening
  // sentence ("APA (…) is the most widely used citation style in the social
  // sciences"). Leaving them in the data means an English sentence in the first
  // paragraph of an otherwise translated page. `styleTagline()`
  // (src/lib/i18n/data-labels.ts) falls back to the data file's own value.
  styleTaglines: {
    apa: "El estilo de cita más utilizado en las ciencias sociales",
    mla: "El estilo de cita estándar para las humanidades",
    chicago:
      "La guía de estilo más consultada de Estados Unidos para autores, editores y editoriales",
    harvard:
      "El estilo de referencia más popular en las universidades del Reino Unido y Australia",
    vancouver: "El estilo de cita estándar para revistas médicas y biomédicas",
    ieee: "El estilo de cita de referencia para ingeniería y ciencias de la computación",
    ama: "El estándar de cita de las revistas médicas estadounidenses",
    turabian: "La versión pensada para estudiantes del estilo Chicago",
    cse: "El estilo de cita preferido en las ciencias naturales y físicas",
    bibtex:
      "El formato de bibliografía para usuarios de LaTeX y la maquetación académica",
    nature:
      "El estilo de cita utilizado por la principal revista multidisciplinar del mundo",
    acs: "El estándar de cita para la química y las ciencias químicas",
    oscola:
      "El estilo de cita jurídica estándar en las facultades de derecho y revistas del Reino Unido",
    bluebook:
      "El sistema de cita jurídica de referencia en los tribunales y revistas de derecho de Estados Unidos",
    aglc: "El estilo de cita jurídica estándar en las facultades de derecho y los tribunales de Australia",
    asa: "El estilo de cita estándar para la sociología y la investigación social",
    apsa: "El estilo de cita para la investigación y las revistas de ciencias políticas",
    aaa: "El estilo de cita estándar para la investigación antropológica",
    nlm: "El estándar de cita utilizado por PubMed y la Biblioteca Nacional de Medicina de EE. UU.",
    acm: "El estilo de cita para las conferencias de la ACM y la investigación en computación",
    aps: "El estilo de cita de Physical Review y las revistas de la APS",
    aip: "El estilo de cita de AIP y las revistas de física",
    cell: "El estilo de cita de Cell Press y las principales revistas de biología",
    science:
      "El estilo de cita de la revista Science y las publicaciones de la AAAS",
    plos: "El estilo de cita de las revistas de acceso abierto de PLOS",
    bmj: "El estilo de cita de BMJ y las principales revistas médicas británicas",
    lancet: "El estilo de cita de la familia de revistas médicas The Lancet",
    jama: "El estilo de cita de JAMA y las revistas de la JAMA Network",
    elsevierHarvard:
      "La variante del estilo Harvard utilizada en las revistas de Elsevier",
    springer: "El estilo de cita de las revistas y libros de Springer Nature",
  },

  // The 105 style-versus-style pages at `/<locale>/compare/<slug>`.
  //
  // The two style names arrive as placeholder values and are never translated. The
  // facts in the table come from `styleFacts` above and the taglines from
  // `styleTaglines`; only `fullName` stays in `src/data/style-pages.ts`, because an
  // official name is the same in every language.
  comparePage: {
    notFoundTitle: "No encontrado",
    metaTitle: "{a} vs. {b} - Diferencias clave explicadas [2026]",
    metaDescription:
      "Compara los estilos de cita {a} y {b} lado a lado. Conoce las diferencias clave en el formato, las citas en el texto y las listas de referencias para elegir el estilo adecuado para tu trabajo.",
    ogTitle: "{a} vs. {b}",
    ogSubtitle:
      "Formato en el texto, entradas de referencia, uso de mayúsculas y qué disciplinas exige cada uno.",
    ogKind: "Comparación de estilos",
    keywordVs: "{a} vs. {b}",
    keywordVersus: "{a} frente a {b}",
    keywordDifference: "diferencia entre {a} y {b}",
    keywordOr: "{a} o {b}",
    keywordComparison: "comparación de estilos de cita",
    breadcrumbCompare: "Comparar estilos",
    breadcrumbCurrent: "{a} vs. {b}",
    backLink: "← Volver a la herramienta de comparación",
    heading: "{a} vs. {b}: diferencias clave",
    intro:
      "{a} y {b} son dos de los estilos de cita más utilizados en el ámbito académico, pero difieren en aspectos importantes. {introA} {introB} Entender las diferencias te ayudará a elegir el formato adecuado para tu trabajo.",
    styleIntro: "{style} ({fullName}) {tagline}.",
    styleIntroFallback:
      "{style} es un estilo de cita popular utilizado en muchas disciplinas académicas.",
    tableHeading: "Comparación lado a lado",
    tableFeature: "Característica",
    tableCitationSystem: "Sistema de cita",
    tableInText: "Formato en el texto",
    tableReferenceList: "Nombre de la lista de referencias",
    tableEdition: "Edición",
    tableCommonFields: "Campos comunes",
    // Title case, for the table cell. `styleLanding.system*` says "Author–date" for the
    // fact list on the style landing pages; both spellings are deliberate.
    systemAuthorDate: "Autor-Fecha",
    systemNumbered: "Numérico",
    systemFootnotes: "Notas al pie",
    // The same three systems inside a sentence ("uses a numbered citation system").
    describeAuthorDate: "autor-fecha entre paréntesis",
    describeNumbered: "numérico",
    describeFootnotes: "basado en notas al pie",
    whenToUseHeading: "Cuándo usar {style}",
    whenToUseBody:
      "{style} ({fullName}) utiliza un sistema de citas {system}, con citas en el texto de tipo {inText} y una lista final llamada «{list}». Se usa sobre todo en {fields}{more}.",
    whenToUseMoreFields: ", y disciplinas afines",
    whenToUseFallback:
      "{style} es un estilo de cita ampliamente reconocido. Consulta las indicaciones de tu trabajo o la revista de destino para confirmar si {style} es el formato exigido.",
    ctaHeading: "Ve la diferencia en acción",
    ctaBodyBefore: "Prueba nuestra",
    ctaBodyLink: "herramienta de comparación interactiva",
    ctaBodyAfter:
      "para comparar las citas en {a} y {b} lado a lado usando tus propias fuentes.",
    ctaCiteIn: "Cita en {style} →",
    faqDifferenceQ: "¿Cuál es la diferencia entre {a} y {b}?",
    faqDifferenceA:
      "{a} y {b} se diferencian en el formato de las citas en el texto, la estructura de la lista de referencias y las disciplinas académicas que los utilizan. {detail}",
    faqDifferenceDetail:
      "{a} usa citas en el texto de tipo {inTextA} con una lista llamada «{listA}», mientras que {b} usa {inTextB} con «{listB}».",
    faqDifferenceDetailFallback:
      "Cada estilo tiene reglas propias para formatear las citas, organizar las bibliografías y tratar los distintos tipos de fuente.",
    faqBetterQ: "¿Cuál es mejor, {a} o {b}?",
    faqBetterA:
      "Ni {a} ni {b} es intrínsecamente mejor. La elección correcta depende de tu disciplina académica, de los requisitos de tu profesor o de la revista a la que envías tu trabajo. Consulta las indicaciones de tu trabajo o la publicación de destino para determinar qué estilo usar.",
    faqSwitchQ: "¿Puedo cambiar entre {a} y {b}?",
    faqSwitchA:
      "Sí, puedes cambiar entre {a} y {b} con el generador de citas gratuito de CitationEasy. Solo tienes que seleccionar el estilo que quieras y tus citas se reformatearán automáticamente. Aun así, debes usar un solo estilo por trabajo, salvo que tu profesor indique lo contrario.",
  },

  // The ~21 converter-pair pages at `/<locale>/convert/<slug>`.
  //
  // Only the shell is here; each pair's own four strings are in `converterCopy` below.
  // The format names (DOI, ISBN, BibTeX, RIS, CSL-JSON, PubMed ID, arXiv ID, Word) are
  // identifiers, never translated.
  converterPage: {
    notFoundTitle: "Conversor no encontrado",
    metaTitle: "{title} - Gratis e instantáneo",
    ogTitle: "{title}",
    ogKind: "Conversor",
    breadcrumbConverters: "Conversores",
    breadcrumbLabel: "Ruta de navegación",
    whenHeading: "Cuándo lo necesitas",
    relatedHeading: "Conversores relacionados",
    allConverters: "Todos los conversores",
    formatConverterTool: "Conversor de formato de citas",
    faqHowQ: "¿Cómo convierto {input} a {output}?",
    faqHowA: "{intro} Todo es gratis, sin cuenta y sin límite de conversiones.",
    faqPrivacyQ: "¿Se sube mi información a algún sitio?",
    faqPrivacyIdentifier:
      "Cada {input} se busca en el servicio de metadatos público correspondiente (Crossref para DOI, PubMed para PMID, registros bibliotecarios para ISBN), y solo se envía el identificador en sí — nunca tus documentos. Los resultados con formato son tuyos para copiar o descargar; no se almacena nada.",
    faqPrivacyLocal:
      "No — el {input} que pegas o subes se analiza por completo en tu navegador. {detail}",
    faqPrivacyDetailStyle:
      "Solo los campos de la referencia ya analizados (autor, título, año) se envían a nuestro formateador para generar las citas; tu archivo nunca sale de tu dispositivo.",
    faqPrivacyDetailOffline:
      "La conversión se ejecuta totalmente sin conexión en la página — no se envía nada a ningún servidor.",
    faqUsefulQ: "¿Cuándo es útil convertir de {input} a {output}?",
    faqOtherQ: "¿También puedo convertir a otros estilos y formatos?",
    faqOtherA:
      "Sí. El generador de CitationEasy da formato a cualquier fuente en {count} estilos de cita, la bibliografía se exporta a Word, RTF, BibTeX, RIS y CSL-JSON, y el catálogo de conversores enumera todos los pares de conversión directa. Si necesitas reescribir un texto ya formateado de un estilo a otro, usa en su lugar la herramienta Conversor de formato de citas.",
  },

  // The 21 converter pairs' own copy (`CONVERTER_PAIRS` in src/data/converters.ts),
  // keyed by slug: title, meta description, the intro paragraph under the H1, and the
  // "when you need this" paragraph.
  //
  // Four hand-written strings per pair, and `src/data/converters.ts` has a quality rule
  // saying they must stay hand-written — no mail-merged sentences, because mass-templated
  // pages are what earned the site a scaled-content penalty. That rule is about how the
  // copy is authored; it says nothing about which language a reader gets it in. Left in
  // the data file they are the entire body of an otherwise translated page.
  // `converterPairCopy()` (src/lib/i18n/data-labels.ts) returns all four at once and
  // falls back to the data file per field, so a converter added upstream ships English
  // copy rather than a blank page.
  converterCopy: {
    doiToApaTitle: "Conversor de citas de DOI a APA",
    doiToApaMetaDescription:
      "Pega DOI (uno por línea) y obtén referencias en APA 7 al instante, extraídas de los metadatos oficiales de Crossref. Gratis, sin cuenta.",
    doiToApaIntro:
      "Un DOI ya apunta a los metadatos oficiales de la editorial, así que no hace falta escribir una referencia APA a mano. Pega un DOI por línea y cada uno se convierte en una referencia APA 7 completa: autores invertidos, título en minúsculas tipo oración, revista en cursiva y el enlace https://doi.org/ que exige APA.",
    doiToApaUseCase:
      "El momento típico: tu profesor pide una lista de referencias en APA y solo tienes el montón de DOI que guardaste mientras leías. Pega el montón, copia la lista.",
    doiToMlaTitle: "Conversor de citas de DOI a MLA",
    doiToMlaMetaDescription:
      "Convierte DOI en entradas de obras citadas en MLA 9 automáticamente con los metadatos de Crossref. Pega un DOI por línea — gratis, sin instalar nada.",
    doiToMlaIntro:
      "MLA 9 trata un artículo de revista como una cadena de contenedores —título del artículo entre comillas, revista en cursiva, volumen, número, año, páginas— y montarla a mano es tedioso. Una búsqueda por DOI rellena cada campo con el registro de la editorial, así que la entrada de obras citadas sale bien a la primera.",
    doiToMlaUseCase:
      "Útil cuando una asignatura de humanidades exige MLA pero tus fuentes vienen de bases de datos de ciencias que solo te dan DOI.",
    doiToBibtexTitle: "Conversor de DOI a BibTeX",
    doiToBibtexMetaDescription:
      "Convierte DOI en entradas BibTeX para LaTeX y Overleaf. Pega un DOI por línea, descarga un .bib listo — gratis y sin límite.",
    doiToBibtexIntro:
      "Todo trabajo en LaTeX acaba necesitando su archivo .bib. Pega los DOI, uno por línea, y obtén entradas @article limpias con claves de cita generadas automáticamente, listas para Overleaf, natbib o biblatex — sin copiar campos a mano desde la web de la editorial.",
    doiToBibtexUseCase:
      "El flujo clásico: un revisor te manda tres artículos que citar, tienes sus DOI y la fecha límite es esta noche. Pega, descarga y \\cite sin más.",
    doiToRisTitle: "Conversor de DOI a RIS",
    doiToRisMetaDescription:
      "Convierte DOI en un archivo RIS que Zotero, Mendeley y EndNote importan directamente. Pega los DOI uno por línea — gratis, sin registro.",
    doiToRisIntro:
      "RIS es el formato de intercambio que importa cualquier gestor de referencias. Pega los DOI y descarga un archivo .ris cuyos campos TY/AU/TI/T2 vienen directos de Crossref — luego usa Archivo → Importar en Zotero, Mendeley o EndNote y las entradas llegan totalmente estructuradas.",
    doiToRisUseCase:
      "Perfecto para migrar una lista de DOI recopilada a mano a un gestor de referencias como es debido, sin volver a escribir ni un nombre de autor.",
    isbnToApaTitle: "Conversor de citas de ISBN a APA",
    isbnToApaMetaDescription:
      "Convierte ISBN en referencias de libros en APA 7 automáticamente. Pega un ISBN por línea y copia la lista con formato — gratis, sin cuenta.",
    isbnToApaIntro:
      "El ISBN de la página de créditos de un libro es todo lo que necesitas para su referencia en APA. Cada ISBN se busca en metadatos bibliotecarios y se formatea en estilo APA 7 —autor, año, título en cursiva y editorial—, incluida la información de edición cuando el registro la trae.",
    isbnToApaUseCase:
      "La forma más rápida de citar una pila de libros de biblioteca: abre cada uno por la página de créditos, escribe los ISBN y pega la lista aquí.",
    isbnToMlaTitle: "Conversor de citas de ISBN a MLA",
    isbnToMlaMetaDescription:
      "Convierte ISBN en entradas de obras citadas en MLA 9 con solo pegarlos. Búsqueda de ISBN gratis con salida ya formateada — sin registro.",
    isbnToMlaIntro:
      "Las entradas de libro en MLA 9 son breves —autor, título en cursiva, editorial, año— pero la inversión del autor y la puntuación siguen dando problemas. Una búsqueda por ISBN devuelve el registro real del catálogo del libro y lo formatea según la plantilla de elementos básicos del manual MLA.",
    isbnToMlaUseCase:
      "Ideal para ensayos de literatura: cita cada novela y cada monografía crítica de tu mesa escribiendo solo los números de la contraportada.",
    isbnToBibtexTitle: "Conversor de ISBN a BibTeX",
    isbnToBibtexMetaDescription:
      "Convierte ISBN en entradas BibTeX @book para LaTeX. Pega un ISBN por línea, descarga el archivo .bib — gratis e instantáneo.",
    isbnToBibtexIntro:
      "Los libros son el rincón incómodo de BibTeX: no hay DOI que buscar y las webs de las editoriales rara vez ofrecen exportación a .bib. Este conversor resuelve cada ISBN con metadatos bibliotecarios y genera entradas @book con los campos de autor, título, editorial y año ya rellenos.",
    isbnToBibtexUseCase:
      "Para quienes escriben una tesis: los capítulos de metodología citan DOI de artículos, pero el capítulo teórico cita doce libros. Esto se encarga de los doce libros.",
    pmidToApaTitle: "Conversor de citas de PMID a APA",
    pmidToApaMetaDescription:
      "Convierte identificadores de PubMed en referencias APA 7 automáticamente. Pega los PMID uno por línea y obtén la lista con formato — gratis.",
    pmidToApaIntro:
      "Todo registro de PubMed tiene un PMID, y los programas de psicología y enfermería exigen casi siempre APA. Pega los PMID uno por línea y cada uno se resuelve con los propios metadatos de PubMed en una referencia de revista APA 7 completa, con el enlace del DOI incluido cuando el registro lo tiene.",
    pmidToApaUseCase:
      "Pensado para trabajos de práctica basada en la evidencia: tu estrategia de búsqueda termina en una lista de PMID, y tu trabajo los necesita en APA.",
    pmidToRisTitle: "Conversor de PMID a RIS",
    pmidToRisMetaDescription:
      "Convierte identificadores de PubMed en un archivo RIS para Zotero, Mendeley o EndNote. Pega los PMID uno por línea, descarga el .ris — gratis.",
    pmidToRisIntro:
      "Los flujos de trabajo de revisiones sistemáticas viven en archivos RIS. Pega los PMID de tu búsqueda en PubMed y descarga un .ris listo para tu herramienta de cribado o gestor de referencias — cada registro estructurado a partir de los metadatos oficiales de PubMed, no de texto extraído.",
    pmidToRisUseCase:
      "Útil a media revisión, cuando un compañero te escribe «incluye también estos cinco estudios» con solo los PMID desnudos y tu biblioteca vive en EndNote.",
    arxivToBibtexTitle: "Conversor de arXiv a BibTeX",
    arxivToBibtexMetaDescription:
      "Convierte identificadores de arXiv en entradas BibTeX para trabajos en LaTeX. Pega ID como 2301.12345, uno por línea, y descarga el .bib — gratis.",
    arxivToBibtexIntro:
      "Los preprints se citan constantemente en los campos que avanzan rápido, y los ID de arXiv (como 2301.12345) son cómo circulan. Pega los ID y obtén entradas BibTeX con los autores, el título, el año y el enlace de arXiv del preprint — listas para el archivo .bib de tu trabajo.",
    arxivToBibtexUseCase:
      "Para quienes escriben en aprendizaje automático o física, cuyo apartado de trabajos relacionados es 80 % arXiv: recopila los ID mientras lees y conviértelos de una vez al escribir.",
    bibtexToApaTitle: "Conversor de BibTeX a APA",
    bibtexToApaMetaDescription:
      "Pega o sube un archivo .bib y obtén una lista de referencias en APA 7. Admite archivos con varias entradas — gratis, se procesa en tu navegador.",
    bibtexToApaIntro:
      "A veces el trabajo sale de LaTeX: una revista pide un envío en Word, o un comité quiere la lista de referencias en texto plano. Pega tu BibTeX (o sube el .bib) y cada entrada se analiza en tu navegador y se formatea como una lista de referencias APA 7 lista para pegar en cualquier documento.",
    bibtexToApaUseCase:
      "El especial de las solicitudes de beca: tu lista de publicaciones vive en un archivo .bib y el formulario del organismo financiador pide las referencias en formato APA dentro de un cuadro de texto.",
    bibtexToMlaTitle: "Conversor de BibTeX a MLA",
    bibtexToMlaMetaDescription:
      "Convierte BibTeX en entradas de obras citadas en MLA 9. Pega o sube el archivo .bib y copia la lista con formato — gratis, sin cuenta.",
    bibtexToMlaIntro:
      "BibTeX guarda los campos en bruto; MLA dicta exactamente cómo ordenarlos. Este conversor analiza tus entradas .bib en tu propio navegador y las procesa con la plantilla de MLA 9: inversión del autor, títulos de artículo entre comillas, contenedores en cursiva y la puntuación correcta entre elementos.",
    bibtexToMlaUseCase:
      "Para quienes cruzan disciplinas: tus referencias viven en BibTeX de un trabajo anterior en LaTeX, pero la revista de humanidades a la que envías pide MLA.",
    bibtexToRisTitle: "Conversor de BibTeX a RIS",
    bibtexToRisMetaDescription:
      "Convierte un archivo .bib a RIS para Zotero, Mendeley o EndNote. Pega o sube BibTeX, descarga el .ris — gratis y sin conexión.",
    bibtexToRisIntro:
      "Pasar de las herramientas de LaTeX a un gestor de referencias implica traducir las entradas @article/@book de BibTeX a las etiquetas TY/AU/TI de RIS. La conversión se hace por completo en tu navegador —con las correspondencias de campos incluidas— y te entrega un .ris que cualquier gestor importa.",
    bibtexToRisUseCase:
      "El desencadenante típico: un nuevo coautor trabaja con Zotero, vuestras referencias compartidas son un .bib en el repositorio y alguien tiene que tender el puente.",
    bibtexToCslJsonTitle: "Conversor de BibTeX a CSL-JSON",
    bibtexToCslJsonMetaDescription:
      "Convierte BibTeX a CSL-JSON para Pandoc y Quarto. Pega o sube el .bib, descarga references.json — gratis, en tu navegador.",
    bibtexToCslJsonIntro:
      "Pandoc y Quarto citan directamente desde CSL-JSON, y convertir tu .bib existente elimina una pieza móvil de la compilación. Las entradas se analizan en tu navegador y salen como un array CSL-JSON limpio, la forma exacta que espera --bibliography=references.json.",
    bibtexToCslJsonUseCase:
      "Para quienes trabajan primero en Markdown y convierten un proyecto antiguo de LaTeX a Quarto: llévate la bibliografía sin editar JSON a mano.",
    bibtexToWordTitle: "Conversor de BibTeX a Word",
    bibtexToWordMetaDescription:
      "Convierte un archivo .bib en un documento de Word con una lista de referencias en APA con sangría francesa. Gratis — el análisis se hace en tu navegador.",
    bibtexToWordIntro:
      "Cuando un archivo .bib tiene que convertirse en un documento —y no en otro formato de datos—, este conversor analiza tu BibTeX en tu propio navegador, formatea cada entrada en APA 7 y descarga un archivo de Word con la lista de referencias ya alfabetizada y con sangría francesa.",
    bibtexToWordUseCase:
      "El movimiento de final de proceso: la revista aceptó el trabajo pero quiere la lista de referencias final como un archivo de Word editable, y tu fuente de verdad es BibTeX.",
    risToApaTitle: "Conversor de RIS a APA",
    risToApaMetaDescription:
      "Pega o sube una exportación RIS y obtén una lista de referencias en APA 7. Funciona con Zotero, Mendeley, EndNote y exportaciones de bases de datos — gratis.",
    risToApaIntro:
      "El botón «Exportar» de cualquier base de datos produce RIS; los trabajos piden APA. Pega la exportación (o sube el .ris) y cada registro TY/AU/TI se analiza en tu navegador y se convierte en una referencia APA 7 — sin necesidad de instalar ningún gestor de referencias.",
    risToApaUseCase:
      "El atajo de la base de datos de la biblioteca: exporta tus resultados de búsqueda como RIS, conviértelos aquí y ahórrate configurar Zotero para un trabajo puntual.",
    risToMlaTitle: "Conversor de RIS a MLA",
    risToMlaMetaDescription:
      "Convierte exportaciones RIS de bases de datos o gestores de referencias en entradas de obras citadas en MLA 9. Pega o sube — gratis, sin registro.",
    risToMlaIntro:
      "JSTOR, ProQuest y EBSCO exportan todos en RIS, y los departamentos de humanidades piden todos MLA. Esta página cierra esa brecha: los registros se analizan en tu navegador y se procesan con la plantilla de elementos básicos de MLA 9, listos para pegar en tu página de obras citadas.",
    risToMlaUseCase:
      "Pensado para la noche antes de entregar el ensayo: diez fuentes exportadas de JSTOR como citations.ris y ninguna gana de formatearlas a mano.",
    risToBibtexTitle: "Conversor de RIS a BibTeX",
    risToBibtexMetaDescription:
      "Convierte RIS a BibTeX para LaTeX y Overleaf. Pega o sube la exportación .ris, descarga un archivo .bib limpio — gratis, en tu navegador.",
    risToBibtexIntro:
      "En dirección a LaTeX: los gestores de referencias y las bases de datos hablan RIS, y la compilación de tu trabajo necesita .bib. Los registros se convierten por completo en tu navegador —con los tipos deducidos de las etiquetas TY y las claves de cita generadas— en un archivo que Overleaf acepta tal cual.",
    risToBibtexUseCase:
      "Habitual en las colaboraciones: quien usa Zotero exporta RIS, quien usa LaTeX lo pasa por esta página, y cada uno conserva su herramienta.",
    risToCslJsonTitle: "Conversor de RIS a CSL-JSON",
    risToCslJsonMetaDescription:
      "Convierte exportaciones RIS a CSL-JSON para flujos de trabajo de Pandoc y Quarto. Pega o sube el .ris, descarga references.json — gratis.",
    risToCslJsonIntro:
      "Para quienes usan Pandoc y Quarto, CSL-JSON es el formato de bibliografía nativo. Dale a esta página el RIS que exportó tu base de datos o gestor y recibe un array JSON estructurado — cada registro analizado en tu navegador, sin subir nada a ningún sitio.",
    risToCslJsonUseCase:
      "Encaja en los flujos de documentación como código: los revisores mandan adjuntos RIS, el archivo de citas de tu repositorio es references.json, y esta página es el adaptador.",
    cslJsonToBibtexTitle: "Conversor de CSL-JSON a BibTeX",
    cslJsonToBibtexMetaDescription:
      "Convierte CSL-JSON (exportaciones de Pandoc, Quarto o Zotero) a BibTeX. Pega o sube el .json, descarga el .bib — gratis, en tu navegador.",
    cslJsonToBibtexIntro:
      "A veces el flujo se invierte: una bibliografía nacida en un proceso de Pandoc o Zotero necesita alimentar una compilación en LaTeX. Este conversor lee el array CSL-JSON en tu navegador y genera entradas BibTeX con los tipos y campos reasignados desde el modelo CSL.",
    cslJsonToBibtexUseCase:
      "Para quien escribe en Quarto pero cuyo coautor insiste en Overleaf: mantén references.json como fuente canónica y genera el .bib bajo demanda.",
    cslJsonToRisTitle: "Conversor de CSL-JSON a RIS",
    cslJsonToRisMetaDescription:
      "Convierte CSL-JSON a RIS para que Zotero, Mendeley o EndNote puedan importarlo. Pega o sube el .json, descarga el .ris — gratis.",
    cslJsonToRisIntro:
      "CSL-JSON es maravilloso dentro de los procesos de Pandoc y opaco para los cuadros de importación de la mayoría de gestores de referencias. Convertirlo a RIS hace que los mismos registros se puedan importar en cualquier sitio — cada elemento asignado a las etiquetas TY/AU/TI en tu navegador, sin enviar nada a ningún servidor.",
    cslJsonToRisUseCase:
      "El problema del traspaso: tu bibliografía es un references.json en un repositorio de git, y tu director la quiere «en EndNote» para el viernes.",
  },

  // The infinite-scroll style list shared by the country and subject pages and by the
  // /citation-styles hub. It is a client component, so these arrive as a prop rather
  // than through `getDictionary` — pulling all 17 dictionaries into the browser bundle
  // to label a search box is the thing `link-path.ts` exists to avoid.
  stylesBrowser: {
    defaultTitle: "Todos los estilos de cita",
    styleSingular: "estilo",
    stylePlural: "estilos",
    found: "de la búsqueda",
    filterPlaceholder: "Filtrar estilos...",
    noMatches: "Ningún estilo coincide con tu búsqueda.",
    loadingMore: "Cargando más estilos...",
    loadMore: "Cargar más ({count} restantes)",
    showingAll: "Mostrando los {count} estilos",
    internationalLabel: "Internacional",
  },

  footer: {
    tagline:
      "Un generador de citas gratuito con {count} estilos. Sin cuenta, sin suscripción de pago y sin límites.",
    freeTools: "Herramientas gratuitas",
    resources: "Recursos",
    legal: "Legal",
    language: "Idioma",
    languageLabel: "Elige tu idioma",
    rights:
      "Las citas se formatean con las definiciones oficiales de Citation Style Language.",
    popularStyles: "Estilos populares",
    about: "Acerca de",
    privacy: "Privacidad",
    terms: "Términos",
    contact: "Contacto",
    copyright: "© {year} CitationEasy. De uso gratuito.",
    poweredBy: "Con la tecnología de",
    allTools: "Todas las herramientas de escritura",
    citationGuides: "Guías de citación",
    allStyles: "Todos los estilos de cita",
    citationGlossary: "Glosario de citación",
    howToCiteAny: "Cómo citar cualquier fuente",
  },

  faq: {
    heading: "Preguntas frecuentes",
    homeQ1: "¿CitationEasy es realmente gratis?",
    homeA1:
      "Sí. CitationEasy es totalmente gratuito: sin registro, sin límites y sin versión de pago. Los {count} estilos de cita están disponibles para todo el mundo.",
    homeQ2: "¿Qué estilos de cita admite?",
    homeA2:
      "Los {count} estilos del proyecto Citation Style Language, incluidos APA 7.ª edición, MLA 9.ª edición, Chicago, Harvard, Vancouver, IEEE, AMA y Turabian.",
    homeQ3: "¿Necesito una cuenta?",
    homeA3:
      "No. CitationEasy funciona íntegramente en tu navegador y tu bibliografía se guarda en tu propio dispositivo. No se sube nada y no hay nada que registrar.",
    homeQ4: "¿Puedo generar las citas en mi idioma?",
    homeA4:
      "Sí. El idioma de las citas es una opción independiente del idioma de la web, así que puedes trabajar en tu idioma y generar las referencias en el idioma en el que escribes el trabajo.",
    styleQ1: "¿El generador de citas {style} es gratuito?",
    styleA1:
      "Sí. Generar citas en {style} es gratis e ilimitado, y no hace falta cuenta. Pega un DOI, una URL, un ISBN o una entrada BibTeX y copia la referencia terminada.",
    styleQ2: "¿Cómo se cita una fuente en {style}?",
    styleA2:
      "Pega el DOI, la URL, el ISBN o la entrada BibTeX de la fuente en el generador de esta página. {style} ya está seleccionado, así que la referencia y su cita en el texto aparecen al instante.",
    styleQ3: "¿Qué precisión tiene el formato {style}?",
    styleA3:
      "Cada referencia se construye a partir de la definición oficial de {style} publicada por el proyecto Citation Style Language —el mismo archivo que usan gestores como Zotero o Mendeley—, no de una aproximación de las reglas.",
  },

  tool: {
    modeLabel: "Modo de entrada de la cita",
    modeAuto: "URL / DOI / ISBN",
    modeBook: "Buscar libros",
    modeArticles: "Buscar artículos",
    modeManual: "Entrada manual",
    errorEnterManually: "Introducir los datos manualmente",
    autoLabel: "Pega una URL, DOI, PMID, ID de arXiv, ISBN o BibTeX",
    autoHint:
      "Detectamos el tipo automáticamente y recuperamos los metadatos. Pega un identificador por línea para citar una lista entera de una vez.",
    generate: "Generar cita",
    generating: "Generando…",
    bookSearchLabel: "Busca por título, autor o ISBN",
    bookSearchEmpty:
      "Aún no hay resultados. Pulsa Buscar para encontrar libros.",
    bookSearchSource:
      "Busca en el catálogo de Open Library, con millones de libros",
    worksSearchLabel: "Busca por título del artículo o autores",
    worksSearchEmpty:
      "Aún no hay resultados. Pulsa Buscar para encontrar artículos.",
    worksSearchSource:
      "Busca en el catálogo de Crossref: artículos de revista, ponencias y capítulos de libro",
    showingResults: "Mostrando {shown} de {total} resultados",
    search: "Buscar",
    searching: "Buscando…",
    bulkResults: "Resultados de citación por lotes",
    copy: "Copiar",
    copied: "¡Copiado!",
    copyHint: "Copia y pega en tu trabajo",
    resultTitle: "Cita con formato",
    edit: "Editar",
    completeFields: "Completar",
    fieldsIncomplete:
      "one:{count} campo incompleto|other:{count} campos incompletos",
    outputLanguage: "Idioma",
    sourceType: "Tipo de fuente",
    fieldAuthors: "Autor(es)",
    fieldAuthorsHint: "Pérez, Juan o Juan Pérez",
    fieldTitle: "Título del artículo o libro",
    fieldBookTitle: "Título del libro",
    fieldBookTitleHint: "Título del libro o la antología",
    fieldEditors: "Editor(es)",
    fieldJournal: "Revista / publicación",
    fieldJournalHint: "Nombre de la revista o publicación",
    fieldPublisher: "Editorial / sitio",
    fieldPublisherHint: "Nombre de la editorial o sitio web",
    fieldDate: "Fecha / año",
    fieldVolume: "Volumen",
    fieldIssue: "Número",
    fieldPages: "Página(s)",
    fieldFormat: "Formato / soporte",
    fieldAuthorsGroup: "Autor colectivo u organización",
    fieldAuthorsGroupHint:
      "Para incluir un grupo junto a personas, escríbalo entre llaves: {R Core Team}, Juan Pérez",
    fieldTranslators: "Traductor(es)",
    fieldInterviewers: "Entrevistador(es)",
    fieldVersion: "Versión",
    fieldCourt: "Tribunal",
    fieldIssuingAuthority: "Autoridad emisora",
    fieldPatentNumber: "Número de patente",
    fieldEpisodeNumber: "Número de episodio",
    fieldLawNumber: "Número de ley o proyecto de ley",
    fieldReportNumber: "Número de informe",
    fieldReviewedTitle: "Título de la obra reseñada",
    fieldReviewedAuthors: "Autor(es) de la obra reseñada",
    inTextCitation: "Cita en el texto",
    quickEdit: "Edición rápida",
    quickEditHint: "Actualiza los metadatos y vuelve a generar la cita.",
    update: "Actualizar",
    saving: "Guardando…",
    fieldAuthor: "Autor",
    fieldDateShort: "Fecha",
    fieldTitleShort: "Título",
  },

  toolCollections: {
    "citation-generators": "Generadores de citas",
    "citation-tools": "Herramientas de citas y referencias",
    "paper-formatting": "Formato de trabajos",
    "essay-writing": "Redacción de ensayos y trabajos",
    "writing-analysis": "Análisis y edición de textos",
    "word-counters": "Contadores y legibilidad",
    vocabulary: "Vocabulario y buscadores de palabras",
    "research-tools": "Investigación y publicación",
    statistics: "Calculadoras estadísticas",
    math: "Calculadoras de matemáticas",
    science: "Calculadoras de ciencias y laboratorio",
    charts: "Gráficos y diagramas",
    economics: "Economía, empresa y finanzas",
    grades: "Calculadoras de GPA y notas",
    study: "Estudio y productividad",
    classroom: "Aula y material imprimible",
    random: "Generadores y selectores aleatorios",
    "text-tools": "Limpieza y formato de texto",
    "fancy-text": "Texto decorativo y símbolos",
    language: "Idiomas y transliteración",
    ciphers: "Códigos y cifrados",
    converters: "Conversores de unidades, fechas y números",
    developer: "Datos, marcado y código",
  },
  bib: {
    title: "Mis bibliografías",
    studioTitle: "Estudio de bibliografías",
    studioProjects: "Proyectos",
    studioExplore: "Explorar",
    studioExploreIntro:
      "Guías, herramientas y todos los estilos de cita a un clic.",
    studioStyleGuide: "Guía del estilo {style}",
    studioStyleGuideHint:
      "Reglas, ejemplos resueltos y formato de cita en el texto",
    studioGenerator: "Generador de citas",
    studioNewProject: "Nuevo proyecto",
    studioSwitchProject: "Cambiar de proyecto",
    studioAddSource: "Añadir una fuente",
    studioAddSourceHint:
      "Pega un DOI, URL o ISBN, busca libros y artículos o introduce los datos a mano. Todo lo que guardes se añade a este proyecto.",
    studioDoneAdding: "Terminar",
    studioReferences: "Referencias",
    studioReferenceCount: "{count} referencias",
    studioInspectorTitle: "Detalles de la referencia",
    studioInspectorEmpty:
      "Selecciona una referencia para ver su cita en el texto, notas y detalles de la fuente.",
    studioSavedLocally: "Guardado en este navegador",
    studioOpen: "Abrir el Estudio de bibliografía",
    studioViewInStudio: "Ver en el Estudio",
    studioFooterSaved: "{count} guardadas en tu Estudio de bibliografía",
    studioFooterEmpty:
      "Las citas guardadas forman una bibliografía completa en el Estudio",
    studioClearSelection: "Quitar selección",
    studioDeleteSelected: "Eliminar seleccionadas",
    studioDeleteSelectedBody:
      "¿Eliminar {count} citas de «{name}»? Esta acción no se puede deshacer.",
    studioDeletedMany: "{count} citas eliminadas",
    studioMoveTo: "Mover a",
    studioCopyTo: "Copiar a",
    studioSearchShortcut: "Pulsa / para buscar",
    studioStyle: "Estilo",
    studioCopyEntry: "Copiar entrada",
    studioEntryCopied: "Entrada copiada",
    studioSourceDetails: "Detalles de la fuente",
    studioEmptyTitle: "Este proyecto está vacío",
    studioEmptyBody:
      "Añade tu primera fuente arriba o importa una biblioteca BibTeX, RIS o CSL-JSON existente.",
    studioBackup: "Copia de seguridad",
    sharedTitle: "Bibliografía compartida",
    save: "Guardar",
    saving: "Guardando…",
    saved: "Guardado",
    searchPlaceholder: "Buscar citas…",
    empty: "Aún no has guardado ninguna cita",
    emptySearch: "Ninguna cita coincide con tu búsqueda.",
    countIn: "Citas en {name}: {count}",
    savedOn: "Guardada el {date}",
    emptyInBib:
      "Todavía no hay citas en esta bibliografía. Genera y guarda citas, o importa una biblioteca BibTeX, RIS o CSL-JSON.",
    savedToast: "Cita guardada en la bibliografía",
    copyAll: "Copiar todo",
    copyAllTitle: "Copiar todas las citas",
    export: "Exportar",
    exportTitle: "Exportar la bibliografía",
    import: "Importar",
    importTitle: "Importar referencias",
    importLabel: "Importar referencias (BibTeX, RIS o CSL-JSON)",
    importing: "Importando…",
    importFailed: "La importación falló. Inténtalo de nuevo.",
    importSummary:
      "Importadas: {added} · Duplicados omitidos: {duplicates} · Sobre el límite: {overLimit} · Fallidas: {failed}",
    importUnreadable: "No se pudo leer el archivo.",
    importUnparseable: "No se pudieron importar las referencias.",
    importReviewTitle: "Revisar importación",
    importReviewIntro:
      "Revisa {total} referencias {format} antes de formatearlas y guardarlas.",
    importReviewCount: "{included} de {total} incluidas",
    importSelectAll: "Incluir todas",
    importExcludeAll: "Excluir todas",
    importLimitNotice:
      "No se importarán {count} referencias que superan el límite de 200 entradas.",
    importDuplicateExisting: "Posible duplicado de «{title}», ya guardada",
    importDuplicateBatch: "Posible duplicado de «{title}» en esta importación",
    importMissingField: "Falta {field}",
    importUntitled: "Referencia sin título",
    importReview: "Revisar referencias",
    importCommit: "Formatear e importar",
    importBack: "Atrás",
    importOutcomeTitle: "Importación completada",
    importOutcomeSummary:
      "Añadidas: {added} · Duplicados omitidos: {duplicates} · Excluidas: {excluded} · Sobre el límite: {overLimit} · Fallidas: {failed}",
    sortBy: "Ordenar por",
    sortTitle: "Ordenar la bibliografía",
    sortAuthor: "Autor (A–Z)",
    sortNewest: "Fecha de adición (más reciente)",
    sortOldest: "Año (más antiguo primero)",
    sortManual: "Orden manual",
    backupAll: "Copia de seguridad completa (.json)",
    backupDownloaded: "Copia de seguridad completa descargada",
    backupUnreadable: "No se pudo leer el archivo de copia de seguridad.",
    restore: "Restaurar",
    restoreTitle: "Restaurar todos los proyectos bibliográficos",
    restoreWarning:
      "Primero se validará la copia y después se reemplazarán todas las bibliografías guardadas en este navegador. Esta acción no se puede deshacer.",
    restoreChoose: "Elige una copia de CitationEasy (.json)",
    replaceAndRestore: "Reemplazar y restaurar",
    restoreComplete: "Se restauraron todos los proyectos bibliográficos",
    restoreInvalid: "Esta no es una copia de proyecto válida de CitationEasy.",
    selectAllVisible: "Seleccionar todo lo mostrado",
    selectedCount: "{count} seleccionadas",
    clusterOpen: "Citar juntas",
    clusterTitle: "Cita compuesta en el texto",
    clusterDescription:
      "Crea un único grupo de citas con las referencias seleccionadas. El estilo elegido controla el orden, la puntuación y la numeración.",
    clusterTarget: "Salida",
    clusterLocatorOptional: "Localizador opcional",
    clusterBuildCopy: "Crear y copiar la cita",
    clusterBuilding: "Creando la cita…",
    clusterCopied: "Cita compuesta copiada",
    clusterFailed: "No se pudo crear la cita compuesta. Inténtalo de nuevo.",
    destination: "Bibliografía de destino",
    chooseDestination: "Elige una bibliografía…",
    copySelected: "Copiar",
    moveSelected: "Mover",
    copyComplete:
      "Se copiaron {count} citas. Se omitieron {skipped} duplicados.",
    moveComplete:
      "Se movieron {count} citas. {skipped} duplicados permanecieron en el origen.",
    selectCitation: "Seleccionar {title}",
    moveUp: "Subir",
    moveDown: "Bajar",
    deleteTitle: "Eliminar cita",
    deleteConfirm: "¿Eliminar la cita?",
    deleted: "Cita eliminada",
    editTitle: "Editar cita",
    close: "Cerrar",
    copiedAll: "¡Todas las citas copiadas!",
    copiedAnnotated: "¡Bibliografía anotada copiada!",
    copiedBibtex: "¡BibTeX copiado!",
    copiedBulleted: "¡Lista con viñetas copiada!",
    copiedNumbered: "¡Lista numerada copiada!",
    copiedHtml: "¡Fragmento HTML copiado!",
    downloadedWord: "Documento de Word descargado",
    downloadedRtf: "Archivo RTF descargado",
    downloadedRis: "Archivo RIS descargado",
    downloadedCsl: "Archivo CSL-JSON descargado",
    downloadedAnnotated: "Bibliografía anotada descargada",
    copyFailed: "No se pudo copiar al portapapeles",
    shareCopied:
      "¡Enlace copiado! Cualquiera que lo tenga puede ver e importar esta lista.",
    shareFailed: "No se pudo crear el enlace para compartir",
    regenerateFailed: "No se pudo regenerar la cita",
    updateFailed: "No se pudo actualizar la cita",
    fieldAuthor: "Autor",
    fieldTitle: "Título",
    fieldDate: "Fecha",
    fieldPublisher: "Editorial",
    fieldVolume: "Volumen",
    fieldIssue: "Número",
    fieldPages: "Página(s)",
    fieldUrl: "URL",
    fieldAccessed: "Fecha de consulta",
    fieldEditor: "Editor",
    fieldEvent: "Lugar del evento",
    noteLabel: "Anotación sobre esta fuente",
    noteEdit: "Editar esta nota",
    notePlaceholder:
      "Resume la fuente, valórala y explica cómo encaja en tu trabajo…",
    noteRemoved: "Nota eliminada",
    noteSave: "Guardar nota",
    noteCancel: "Cancelar",
    noteAdd: "Añadir nota",
    noteWords: "{count} palabras",
    noteSaved: "Nota guardada ({count} palabras)",
    noteClickToEdit: "haz clic para editar",
    inTextCopy: "Copiar la cita en el texto de esta fuente",
    inTextSelect: "Selecciona la cita de abajo para copiarla.",
    inTextFailed:
      "No se pudo construir la cita en el texto. Inténtalo de nuevo.",
    locator: "Localizador",
    locatorType: "Tipo de localizador",
    locatorPage: "Página",
    locatorChapter: "Capítulo",
    locatorSection: "Sección",
    locatorParagraph: "Párrafo",
    locatorLine: "Línea",
    locatorVerse: "Versículo",
    reformat: "Reformatear",
    reformatTitle: "Reformatear todas las citas en otro estilo",
    reformatHeading: "Reformatear todo en un solo estilo",
    reformatChoose: "Elige un estilo…",
    reformatFailed: "El reformateo falló. Tu bibliografía no ha cambiado.",
    sortOrder: "Criterio de orden",
    copyToClipboard: "Copiar al portapapeles",
    numberedList: "Lista numerada",
    bulletedList: "Lista con viñetas",
    htmlSnippet: "Fragmento HTML",
    copyWithAnnotations: "Copiar con anotaciones",
    wordAnnotated: "Documento de Word anotado (.doc)",
    downloadFile: "Descargar archivo",
    wordDocument: "Documento de Word (.doc)",
    copyShareLink: "Copiar enlace para compartir",
    print: "Imprimir…",
    printFailed: "Ventana emergente bloqueada; permítelas para imprimir",
    font: "Fuente",
    sitePublication: "Sitio / nombre de la publicación",
    importIntro:
      "Pega o sube un archivo BibTeX (.bib), RIS (.ris) o CSL-JSON (.json)",
    importUpload: "Sube un archivo .bib, .ris o .json",
    importPaste: "O pega BibTeX, RIS o CSL-JSON",
    cancel: "Cancelar",
    done: "Listo",
    delete: "Eliminar",
    deleteBody:
      "Esto eliminará esta cita de tu bibliografía de forma permanente.",
    editBody: "Actualiza los metadatos y guarda para regenerar la cita.",
    reformatExplain:
      "Reconstruye las {count} entradas a partir de sus datos guardados: no se descarga nada de nuevo y no se pierde ninguna fuente.",
    inTextHeading: "Cita en el texto",
    inTextLocatorHint:
      "Añade una página u otro localizador para fijar la cita en un punto de la fuente: la mayoría de los estilos lo exigen cuando citas textualmente. Déjalo en blanco para la forma simple.",
    inTextCopyButton: "Copiar la cita en el texto",
    mixedStyles: "Esta bibliografía mezcla {count} estilos de cita ({styles}).",
    mixedStylesAction:
      "Usa {action} para unificarlos todos en un solo estilo antes de entregarla.",
    selectBibliography: "Seleccionar bibliografía",
    newBibliography: "Nueva bibliografía",
    moreOptions: "Más opciones",
    rename: "Cambiar nombre",
    removeDuplicates: "Eliminar duplicados",
    cannotDeleteOnly: "No puedes eliminar tu única bibliografía",
    newBibliographyBody: "Crea una bibliografía para otro trabajo o proyecto.",
    bibliographyName: "Nombre de la bibliografía",
    create: "Crear",
    renameBibliography: "Cambiar nombre de la bibliografía",
    renameBibliographyBody: "Escribe un nuevo nombre para «{name}».",
    newName: "Nuevo nombre",
    duplicateRemovedOne: "Se eliminó {count} duplicado",
    duplicateRemovedMany: "Se eliminaron {count} duplicados",
    noDuplicates: "No se encontraron referencias duplicadas",
    removeDuplicatesConfirm: "¿Eliminar referencias duplicadas?",
    removeDuplicatesBody:
      "Se buscarán en «{name}» fuentes iguales (por DOI, ISBN o título, año y autor) y se conservará una copia. Las referencias que no puedan compararse no cambiarán.",
    deleteBibliographyConfirm: "¿Eliminar la bibliografía?",
    deleteBibliographyBody:
      "Esto eliminará permanentemente «{name}» y sus {count} citas.",
  },

  bibliographyPage: {
    metaTitle: "Estudio de bibliografías - organiza, anota y exporta citas",
    metaDescription:
      "Crea y gestiona proyectos bibliográficos privados en tu navegador. Busca, ordena, anota, edita, reformatea, importa, exporta y comparte citas sin una cuenta.",
    keyword1: "creador de bibliografías",
    keyword2: "gestor de citas",
    keyword3: "herramienta de bibliografía anotada",
    ogTitle: "Estudio de bibliografías",
    ogDescription:
      "Un espacio privado a página completa para organizar, formatear, anotar, importar y exportar citas.",
    ogImageTitle: "Estudio de bibliografías",
    ogImageSubtitle:
      "Organiza, anota, reformatea, exporta y comparte tus referencias.",
    ogKind: "Espacio de citas",
    breadcrumb: "Estudio de bibliografías",
    eyebrow: "Espacio privado en el navegador",
    lead: "Gestiona en un solo lugar todas las referencias de un trabajo. Tus proyectos permanecen en este navegador salvo que decidas exportarlos o compartirlos.",
    privacyTitle: "Privado de forma predeterminada",
    privacyBody:
      "Los proyectos y las anotaciones usan el almacenamiento local del navegador. Los enlaces compartidos incluyen las citas, pero omiten intencionadamente las notas privadas.",
    workflowTitle: "De la lista de fuentes al trabajo final",
    workflowBody:
      "Cambia de proyecto, busca y ordena referencias, añade anotaciones o localizadores, corrige metadatos y reformatea toda la lista en un solo estilo.",
    formatsTitle: "Importa referencias y llévalas donde quieras",
    formatsBody:
      "Importa BibTeX, RIS o CSL-JSON. Copia listas formateadas o exporta Word, RTF, RIS, CSL-JSON, HTML, BibTeX y versiones anotadas.",
  },

  // The 95-term glossary: /glossary and /glossary/[slug].
  //
  // The chrome only. Each term's own prose — `short`, the `body` paragraphs, the
  // example — is ~10,600 words that cannot live in a flat string map and must not be
  // copied verbatim into sixteen files; it lives in `src/content/glossary/<locale>.json`
  // and is read through `lib/localized-content.ts`.
  glossaryPage: {
    metaTitle: "{term} - definición y ejemplos",
    notFoundTitle: "Término no encontrado",
    keywordDefinition: "definición de {term}",
    keywordWhatIs: "qué es {term}",
    ogTitle: "{term} - glosario de CitationEasy",
    ogKind: "Glosario de citación",
    breadcrumb: "Glosario",
    alsoCalled: "También llamado: {aliases}",
    doItHeading: "Hazlo en CitationEasy",
    seeAlsoHeading: "Ver también",
    moreInHeading: "Más en {category}",
    navLabel: "Navegación del glosario",
    allTermsLink: "Los {count} términos del glosario",
    generatorLink: "Generador de citas gratuito",
    termSetName: "Glosario de citación de CitationEasy",
    hubMetaTitle: "Glosario de citación - {count} términos explicados",
    hubMetaDescription:
      "Definiciones claras de {count} términos de citación y escritura académica: et al., ibid., DOI, sangría francesa, patchwriting, versión de registro y más, cada uno con ejemplos.",
    hubKeyword1: "glosario de citación",
    hubKeyword2: "términos de citación",
    hubKeyword3: "glosario de escritura académica",
    hubKeyword4: "qué significa et al",
    hubKeyword5: "qué es un DOI",
    hubKeyword6: "terminología de referencias",
    hubOgTitle: "Glosario de citación",
    hubOgDescription:
      "Definiciones de {count} términos de citación y escritura académica, con ejemplos.",
    hubOgImageTitle: "Glosario de citación",
    hubOgImageSubtitle:
      "{count} términos de citación y escritura académica, explicados de forma clara y sencilla con ejemplos.",
    hubOgKind: "Referencia",
    hubTermSetDescription:
      "Definiciones de {count} términos de citación, referencias y escritura académica.",
    hubCollectionName: "Glosario de citación",
    hubCollectionDescription:
      "Definiciones claras de {count} términos de citación y escritura académica.",
    hubHeading: "Glosario de citación",
    hubIntro:
      "{count} términos de citación y escritura académica, explicados de forma clara y sencilla con ejemplos: las abreviaturas que aparecen en las guías de estilo, las partes de las que se compone una referencia, los identificadores que fijan una fuente y el vocabulario de la publicación académica.",
    hubJumpLabel: "Ir a la letra",
    hubBrowseHeading: "Explorar por tema",
    hubAllHeading: "Todos los términos de la A a la Z",
    hubWorkHeading: "Pon los términos en práctica",
    hubWorkGenerator: "Generador de citas gratuito",
    hubWorkStyles: "Guías de estilos de cita",
    hubWorkHowTo: "Cómo citar cualquier fuente",
    hubWorkCompare: "Compara estilos de cita",
    hubWorkTools: "Herramientas de escritura y citación",
    hubWorkFaq: "Preguntas frecuentes sobre citación",
  },

  // /faq — the 17-question hub. The questions and answers themselves are in
  // `src/data/faq.ts` (the one source the API, the MCP tool and /md also read); this is
  // the page around them.
  faqPage: {
    metaTitle:
      "Preguntas frecuentes sobre citación - Respuestas rápidas a las dudas más comunes",
    metaDescription:
      "Respuestas rápidas y precisas a las preguntas sobre citación que realmente hacen los estudiantes: DOI, fuentes sin autor, et al., sangría francesa, cómo citar la IA, normas sobre plagio, APA frente a MLA y más, con enlaces a las guías completas.",
    ogTitle: "Preguntas frecuentes sobre citación",
    ogDescription:
      "Respuestas rápidas a las preguntas más habituales sobre citación, desde los DOI y et al. hasta cómo citar ChatGPT.",
    ogImageTitle: "Preguntas y respuestas sobre citación",
    ogImageSubtitle:
      "Respuestas breves y precisas a las preguntas que realmente hacen los estudiantes sobre citación.",
    ogKind: "Preguntas frecuentes",
    breadcrumbLabel: "Ruta de navegación",
    breadcrumbHome: "Inicio",
    breadcrumbCurrent: "Preguntas frecuentes sobre citación",
    heading: "Preguntas frecuentes sobre citación",
    intro:
      "Respuestas rápidas y precisas a las preguntas sobre citación que realmente hacen los estudiantes, con enlaces a la guía o herramienta completa cuando necesites más detalle.",
    contentsLabel: "Contenido de las preguntas frecuentes",
    stillStuckHeading: "¿Sigues con dudas?",
    stillStuckBody:
      "Explora la [biblioteca completa de guías](/guides), consulta las [hojas de referencia rápida por estilo](/cheat-sheets), busca un término en el [glosario de citación](/glossary), [busca en todo el sitio](/search) o simplemente [genera la cita](/) y deja que el formateador aplique las reglas por ti.",
  },

  // /cheat-sheets and its three style sheets. The citation FORMATS in them
  // ("Author, A. A. (Year)…") are the styles' own templates and stay as they are in
  // every language, exactly as the per-style examples do; the labels around them do not.
  cheatSheets: {
    hubMetaTitle:
      "Hojas de referencia rápida de estilos de cita - Guías de consulta rápida",
    hubMetaDescription:
      "Hojas de referencia rápida para los estilos de cita APA, MLA y Chicago. Guías aptas para imprimir con ejemplos de los tipos de fuente más comunes.",
    hubBreadcrumbHome: "Inicio",
    hubBreadcrumbCurrent: "Hojas de referencia rápida de estilos de cita",
    hubBackLink: "← Volver al generador",
    hubHeading: "Hojas de referencia rápida de estilos de cita",
    hubIntro:
      "Guías de consulta rápida para los estilos de cita más populares. Aptas para imprimir y siempre actualizadas.",
    hubApaTitle: "Hoja de referencia rápida de APA 7.ª edición",
    hubApaDesc: "Psicología, ciencias sociales, educación, enfermería",
    hubMlaTitle: "Hoja de referencia rápida de MLA 9.ª edición",
    hubMlaDesc: "Literatura, lenguas, humanidades, estudios culturales",
    hubChicagoTitle: "Hoja de referencia rápida de estilo Chicago",
    hubChicagoDesc: "Historia, historia del arte, filosofía, religión",
    backToSheets: "← Volver a las hojas de referencia rápida",
    generalRules: "Normas generales",
    inTextCitations: "Citas en el texto",
    colSituation: "Situación",
    colFormat: "Formato",
    colExample: "Ejemplo",
    book: "Libro",
    journalArticle: "Artículo de revista",
    website: "Página web",
    apaMetaTitle:
      "Hoja de referencia rápida de APA 7.ª edición - Guía de consulta rápida",
    apaMetaDescription:
      "Hoja de referencia rápida de citas en APA 7.ª edición con ejemplos para libros, revistas, páginas web y más. Consulta rápida apta para imprimir.",
    apaHeading: "Hoja de referencia rápida de APA 7.ª edición",
    apaLead: "Consulta rápida de los formatos de cita en APA más comunes.",
    apaRule1:
      'Usa **sangría francesa** (primera línea alineada a la izquierda, líneas siguientes con sangría de 0,5" / 1,27 cm)',
    apaRule2: "Usa doble espacio en todas las entradas",
    apaRule3: "Ordena alfabéticamente por el apellido del autor",
    apaRule4: "Pon en cursiva los títulos de libros, revistas y volúmenes",
    apaRule5: "Usa el DOI cuando esté disponible",
    apaRow1Situation: "1 autor",
    apaRow1Format: "(Author, Year)",
    apaRow1Example: "(Smith, 2024)",
    apaRow2Situation: "2 autores",
    apaRow2Format: "(Author & Author, Year)",
    apaRow2Example: "(Smith & Jones, 2024)",
    apaRow3Situation: "3 o más autores",
    apaRow3Format: "(First Author et al., Year)",
    apaRow3Example: "(Smith et al., 2024)",
    apaRow4Situation: "Cita textual",
    apaRow4Format: "(Author, Year, p. #)",
    apaRow4Example: "(Smith, 2024, p. 45)",
    apaRow5Situation: "Sin autor",
    apaRow5Format: '("Title," Year)',
    apaRow5Example: '("Study Results," 2024)',
    apaReferenceHeading: "Ejemplos de la lista de referencias",
    apaBookExample:
      "Author, A. A. (Year). *Title of work: Capital letter also for subtitle*. Publisher. https://doi.org/xxxxx",
    apaJournalExample:
      "Author, A. A., & Author, B. B. (Year). Title of article. *Title of Periodical, Volume*(Issue), Page–Page. https://doi.org/xxxxx",
    apaWebsiteExample:
      "Author, A. A. (Year, Month Day). *Title of page*. Site Name. https://www.example.com/page",
    apaChapterLabel: "Capítulo de libro editado",
    apaChapterExample:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), *Title of book* (pp. #–#). Publisher.",
    apaConferenceLabel: "Ponencia de congreso",
    apaConferenceExample:
      "Author, A. A. (Year, Month Days). *Title of paper* [Paper presentation]. Conference Name, Location.",
    apaDifferencesHeading: "Diferencias clave con respecto a APA 6.ª edición",
    apaDifference1: 'Ya no se usa "Retrieved from" antes de las URL',
    apaDifference2: "Incluye el DOI en formato de enlace https://doi.org/",
    apaDifference3:
      "Se enumeran hasta 20 autores antes de usar puntos suspensivos",
    apaDifference4: "Ya no se exige el lugar de publicación de la editorial",
    apaDifference5:
      'Se usa "they" en singular como pronombre neutro (norma de la APA para el inglés)',
    apaCtaText: "Genera citas perfectas en APA automáticamente",
    apaCtaButton: "Generador de citas APA →",
    mlaMetaTitle:
      "Hoja de referencia rápida de MLA 9.ª edición - Guía de consulta rápida",
    mlaMetaDescription:
      "Hoja de referencia rápida de citas en MLA 9.ª edición con ejemplos para libros, artículos, páginas web y más. Consulta rápida apta para imprimir.",
    mlaHeading: "Hoja de referencia rápida de MLA 9.ª edición",
    mlaLead: "Consulta rápida de los formatos de cita en MLA más comunes.",
    mlaRule1: "Usa **sangría francesa**",
    mlaRule2: "Usa doble espacio en todas las entradas",
    mlaRule3: "Ordena alfabéticamente por el apellido del autor",
    mlaRule4: 'Titula la página "Works Cited" (obras citadas)',
    mlaRule5:
      "Pon en cursiva los títulos de los contenedores (libros, revistas, páginas web)",
    mlaRow1Situation: "1 autor",
    mlaRow1Format: "(Author Page)",
    mlaRow1Example: "(Smith 45)",
    mlaRow2Situation: "2 autores",
    mlaRow2Format: "(Author and Author Page)",
    mlaRow2Example: "(Smith and Jones 45)",
    mlaRow3Situation: "3 o más autores",
    mlaRow3Format: "(First Author et al. Page)",
    mlaRow3Example: "(Smith et al. 45)",
    mlaRow4Situation: "Sin autor",
    mlaRow4Format: '("Title" Page)',
    mlaRow4Example: '("Study Results" 45)',
    mlaRow5Situation: "Sin número de página",
    mlaRow5Format: "(Author)",
    mlaRow5Example: "(Smith)",
    mlaWorksCitedHeading: "Ejemplos de obras citadas",
    mlaBookExample: "Last, First. *Title of Book*. Publisher, Year.",
    mlaJournalExample:
      'Last, First. "Title of Article." *Journal Title*, vol. #, no. #, Year, pp. #–#.',
    mlaWebsiteExample:
      'Last, First. "Title of Page." *Website Name*, Day Month Year, URL.',
    mlaAnthologyLabel: "Antología/Libro editado",
    mlaAnthologyExample:
      'Last, First. "Title of Essay." *Title of Collection*, edited by First Last, Publisher, Year, pp. #–#.',
    mlaCoreHeading: "Elementos básicos de MLA (en orden)",
    mlaCore1: "Autor.",
    mlaCore2: '"Título de la fuente."',
    mlaCore3: "*Título del contenedor*,",
    mlaCore4: "Colaboradores,",
    mlaCore5: "Versión,",
    mlaCore6: "Número,",
    mlaCore7: "Editorial,",
    mlaCore8: "Fecha de publicación,",
    mlaCore9: "Ubicación (páginas, URL, DOI).",
    mlaCtaText: "Genera citas perfectas en MLA automáticamente",
    mlaCtaButton: "Generador de citas MLA →",
    chicagoMetaTitle:
      "Hoja de referencia rápida de estilo Chicago - Guía de consulta rápida",
    chicagoMetaDescription:
      "Hoja de referencia rápida de citas del Chicago Manual of Style con ejemplos de los sistemas de notas y bibliografía y autor-fecha. Consulta rápida apta para imprimir.",
    chicagoHeading: "Hoja de referencia rápida de estilo Chicago",
    chicagoLead:
      "Consulta rápida de los formatos de cita del Chicago Manual of Style (18.ª edición).",
    chicagoSystemsHeading: "Los dos sistemas de Chicago",
    chicagoColNb: "Notas y bibliografía (NB)",
    chicagoColAd: "Autor-fecha (AD)",
    chicagoRow1Nb: "Notas al pie/notas finales + bibliografía",
    chicagoRow1Ad: "Citas entre paréntesis + lista de referencias",
    chicagoRow2Nb: "Historia, artes, humanidades",
    chicagoRow2Ad: "Ciencias, ciencias sociales",
    chicagoRow3Nb: "Primeras notas más detalladas",
    chicagoRow3Ad: "Similar al formato APA",
    chicagoExamplesHeading: "Ejemplos de notas y bibliografía",
    chicagoNoteLabel: "Nota:",
    chicagoBibliographyLabel: "Bibliografía:",
    chicagoBookLabel: "Libro (nota al pie)",
    chicagoBookNote: "1. First Last, *Title of Book* (Publisher, Year), page.",
    chicagoBookBibliography: "Last, First. *Title of Book*. Publisher, Year.",
    chicagoJournalLabel: "Artículo de revista (nota al pie)",
    chicagoJournalNote:
      '1. First Last, "Title of Article," *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoJournalBibliography:
      'Last, First. "Title of Article." *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoWebsiteLabel: "Página web (nota al pie)",
    chicagoWebsiteNote:
      '1. First Last, "Title of Page," Site Name, Month Day, Year, URL.',
    chicagoWebsiteBibliography:
      'Last, First. "Title of Page." Site Name. Month Day, Year. URL.',
    chicagoShortHeading: "Notas abreviadas",
    chicagoShortIntro:
      "Después de la primera cita completa, usa notas al pie abreviadas:",
    chicagoShortExample: "1. Last, *Short Title*, page.",
    chicagoRulesHeading: "Normas clave de formato",
    chicagoRule1:
      "Usa números en superíndice para las referencias de notas al pie en el texto",
    chicagoRule2: "Las entradas de la bibliografía usan sangría francesa",
    chicagoRule3:
      'Usa una nota abreviada en lugar de "Ibid." para citas consecutivas de la misma fuente',
    chicagoRule4:
      "Títulos: los libros y las revistas van en cursiva, y los artículos y capítulos entre comillas",
    chicagoRule5:
      "Indica la fecha de acceso en las fuentes en línea que puedan cambiar",
    chicagoCtaText: "Genera citas perfectas en Chicago automáticamente",
    chicagoCtaButton: "Generador de citas Chicago →",
  },

  // The catalog's page-type labels (`CATALOG_TYPE_LABELS`), which the search page shows
  // as facet chips and beside every result. Layer 2: a fact from `src/lib/**` rather
  // than page chrome, and English until it is mapped through here.
  catalogTypes: {
    core: "Páginas principales",
    styleLanding: "Centros de los estilos de cita más importantes",
    style: "Páginas de referencia de estilos de cita",
    styleExamples: "Páginas de ejemplos de citas",
    styleFormat: "Guías de formato de trabajos",
    styleInText: "Guías de citas en el texto",
    styleAnnotatedBibliography: "Guías de bibliografía anotada",
    styleReferencePage: "Guías de la lista de referencias/obras citadas",
    tool: "Herramientas gratuitas de escritura y citación",
    guide: "Guías educativas",
    guideCategory: "Categorías de guías",
    toolCollection: "Colecciones de herramientas",
    howToCite: "Cómo citar <fuente> en <estilo>",
    comparison: "Comparaciones de estilos de cita",
    converter: "Conversores de identificadores y formatos",
    field: "Estilos de cita por campo académico",
    region: "Estilos de cita por país/región",
    category: "Estilos de cita por categoría temática",
    cheatSheet: "Hojas de referencia rápida de una página",
    glossary: "Glosario de citación y escritura académica",
  },

  // /about
  aboutPage: {
    metaTitle: "Acerca de CitationEasy - Generador de citas gratuito",
    metaDescription:
      "CitationEasy es un generador de citas gratuito que admite {count} estilos. Creado por Samuel Sergi con herramientas de código abierto como Citation Style Language, Citation.js y Next.js.",
    backLink: "← Volver al generador",
    heading: "Acerca de CitationEasy",
    intro:
      "CitationEasy es un generador de citas gratuito y de acceso abierto, creado para estudiantes, investigadores y redactores académicos. Admite **{count} estilos de cita** y ofrece una colección cada vez mayor de herramientas de escritura, guías educativas y recursos de referencia, todo sin necesidad de registro ni suscripción.",
    offerHeading: "Qué ofrecemos",
    offer1:
      "[Generador de citas](/) con {count} estilos, incluidos APA 7.ª ed., MLA 9.ª ed., Chicago 18.ª ed., Harvard, Vancouver, IEEE y miles más",
    offer2:
      "[40 herramientas gratuitas de escritura y citación](/tools): contador de palabras, verificador de legibilidad, validador de DOI, ordenador alfabético de bibliografías y más",
    offer3:
      "[750 guías de cómo citar](/how-to-cite) que cubren todos los tipos de fuente principales en 6 estilos de cita",
    offer4:
      "[Más de 100 guías educativas](/guides) sobre buenas prácticas de citación, escritura académica y habilidades de investigación",
    offer5:
      "[Herramienta de comparación de estilos](/compare), [cuestionario de estilos de cita](/quiz) y [hojas de referencia rápida](/cheat-sheets) para consultas rápidas",
    offer6:
      "7 formatos de exportación: texto sin formato, BibTeX, Word, lista numerada, lista con viñetas, RIS y CSV",
    openSourceHeading: "Creado con software de código abierto",
    openSourceIntro:
      "CitationEasy es posible gracias a varios excelentes proyectos y API de código abierto. Creemos en dar crédito a quien lo merece:",
    openSource1:
      "[Citation Style Language (CSL)](https://citationstyles.org/): el estándar abierto que impulsa nuestros {count} estilos de cita. CSL lo mantiene una comunidad de voluntarios y lo usan gestores de referencias como Zotero, Mendeley y Papers. Las definiciones de los estilos provienen del [repositorio de estilos CSL](https://github.com/citation-style-language/styles) en GitHub.",
    openSource2:
      "[Citation.js](https://citation.js.org/): la biblioteca de JavaScript que procesa las citas y les da formato según los estilos CSL. Creada por Lars Willighagen.",
    openSource3:
      "[API de Open Library](https://openlibrary.org/developers/api): la API gratuita de búsqueda de libros que ofrece Internet Archive, usada para buscar metadatos de libros por título o ISBN.",
    openSource4:
      "[DOI Foundation](https://www.doi.org/): el sistema de identificador de objeto digital (DOI) usado para resolver y validar los DOI.",
    createdHeading: "Creado por",
    createdBody:
      "CitationEasy fue creado y lo mantiene **Samuel Sergi**. El objetivo es simple: ofrecer una herramienta de citación genuinamente gratuita y de alta calidad, sin funciones bloqueadas tras un muro de pago ni necesidad de crear una cuenta.",
    contactPrompt:
      "¿Tienes preguntas, comentarios o sugerencias? Ponte en contacto:",
    contactEmail:
      "Correo electrónico: [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    missionHeading: "Nuestra misión",
    mission1:
      "Las citas precisas no deberían costar dinero. Muchas herramientas de citación se anuncian como gratuitas, pero restringen los formatos de exportación, limitan el número de citas o empujan a los usuarios hacia planes premium. CitationEasy se compromete a ser completamente gratuito: sin registro, sin límites, sin nivel premium.",
    mission2:
      "Todas las funciones de este sitio, desde el generador de citas hasta las herramientas de escritura y las guías educativas, están disponibles para todos sin costo alguno. Creemos que las herramientas de integridad académica deben ser accesibles para todos los estudiantes e investigadores, sin importar su presupuesto.",
  },

  // /contact
  contactPage: {
    metaTitle: "Contacto",
    metaDescription:
      "Ponte en contacto con CitationEasy. ¿Tienes preguntas, comentarios o sugerencias sobre nuestro generador de citas y herramientas de escritura gratuitos? Nos encantaría saber de ti.",
    backLink: "← Volver al generador",
    heading: "Contacto",
    intro:
      "¿Tienes preguntas, comentarios o sugerencias sobre CitationEasy? Nos encantaría saber de ti. Ya sea que hayas encontrado un error, tengas una idea para una nueva función o necesites ayuda con una cita, no dudes en escribirnos.",
    touchHeading: "Ponte en contacto",
    touchBody:
      "La mejor forma de contactarnos es por correo electrónico. Intentamos responder en un plazo de 48 horas.",
    emailLine:
      "**Correo electrónico:** [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    commonHeading: "Preguntas frecuentes",
    commonIntro:
      "Antes de escribirnos, puede que encuentres la respuesta a tu pregunta en uno de estos recursos:",
    resource1:
      "[Guías de citación](/guides): instrucciones paso a paso para citar fuentes en todos los estilos principales",
    resource2:
      "[Guías de cómo citar](/how-to-cite): 750 combinaciones de tipo de fuente y estilo, con ejemplos",
    resource3:
      "[Hojas de referencia rápida](/cheat-sheets): consulta rápida de formato APA, MLA y Chicago",
    resource4:
      "[Herramientas de escritura](/tools): contador de palabras, verificador de legibilidad, validador de DOI y más",
    issueHeading: "Reportar un problema",
    issueIntro:
      "Si has encontrado un error de formato en una cita, una función que no funciona o un estilo incorrecto, incluye todos los detalles posibles al contactarnos:",
    issue1: "El estilo de cita que estabas usando",
    issue2: "El tipo de fuente (libro, página web, artículo de revista, etc.)",
    issue3: "Qué esperabas frente a lo que se generó",
    issue4: "Tu navegador y dispositivo (si es relevante)",
    issueOutro:
      "Esto nos ayuda a identificar y solucionar el problema rápidamente.",
    aboutHeading: "Acerca de CitationEasy",
    aboutBody:
      "CitationEasy es un generador de citas gratuito que admite más de 10.000 estilos de cita. Fue creado por Samuel Sergi con el objetivo de hacer que las citas precisas sean accesibles para todos. Obtén más información en nuestra [página Acerca de](/about).",
  },

  // /ai — the agent and developer docs.
  //
  // The code samples, endpoint paths and MCP tool NAMES are deliberately absent: they are
  // the API's identifiers, served in English by the API itself, and a translated one
  // would document an endpoint that does not exist.
  aiPage: {
    metaTitle:
      "CitationEasy para asistentes y agentes de IA - API, servidor MCP, llms.txt",
    metaDescription:
      "Usa CitationEasy mediante programación: una API JSON gratuita y sin autenticación para citas en {count} estilos, un servidor MCP para agentes, llms.txt, una especificación OpenAPI y vistas en markdown de todas las páginas.",
    keyword1: "API de citación",
    keyword2: "servidor MCP",
    keyword3: "API de generador de citas",
    keyword4: "llms.txt",
    keyword5: "OpenAPI",
    keyword6: "citas para agentes de IA",
    keyword7: "API de citación gratuita",
    webApiDescription:
      "API JSON gratuita y sin autenticación que da formato a citas en {count} estilos de Citation Style Language a partir de una URL, DOI, ISBN, PubMed ID, arXiv ID o metadatos CSL-JSON.",
    breadcrumb: "Para IA y agentes",
    heading: "CitationEasy para asistentes y agentes de IA",
    intro:
      "Todo lo que hay en este sitio está disponible tanto para el software como para las personas: una API JSON gratuita, un servidor MCP, una especificación OpenAPI, un índice llms.txt y una vista en markdown de las {count} páginas. Sin clave, sin cuenta, sin límite de solicitudes propio.",
    whyHeading: "Por qué llamar a la API en lugar de escribir la cita tú mismo",
    whyBody:
      "Dar formato a una cita parece un problema de generación de texto, pero no lo es. Cada estilo codifica cientos de reglas sobre la inversión de autores, las iniciales, las mayúsculas en los títulos, la puntuación de los contenedores, las menciones de edición y la ubicación de la fecha, y difieren de formas que son fáciles de equivocar sutilmente de memoria. CitationEasy ejecuta **citeproc-js** sobre las definiciones oficiales de Citation Style Language (el mismo motor que usan Zotero y Mendeley), así que el resultado es determinista y coincide con lo que espera el sistema de envío de una revista. Los metadatos provienen de Crossref, Open Library y PubMed; no se inventa nada, y un campo que falte en el origen sigue faltando en lugar de rellenarse con una suposición plausible.",
    generateHeading: "Generar una cita",
    generateBody:
      "Envía `inputs` en lugar de `input` para hasta 10 fuentes en una sola llamada, o `entry` con metadatos CSL-JSON que ya tengas para saltarte la búsqueda por completo. Añade `note: true` para obtener la forma de nota al pie. Los fallos por fuente se devuelven asociados a esa fuente, así que un lote nunca falla en su totalidad.",
    mcpHeading: "Conéctate mediante MCP",
    mcpIntro:
      "El punto de conexión del Model Context Protocol en `/api/mcp` habla JSON-RPC 2.0 sobre HTTP en streaming y no necesita credenciales.",
    mcpToolsHeading: "Herramientas que expone",
    endpointsHeading: "Endpoints",
    colEndpoint: "Endpoint",
    colWhat: "Qué hace",
    endpointCite:
      "Da formato a hasta 10 fuentes a la vez. Acepta una URL, DOI, ISBN, PubMed ID, arXiv ID, texto BibTeX/RIS/CSL-JSON o un objeto de entrada CSL-JSON. Devuelve la entrada de referencia y, opcionalmente, la cita en el texto y la nota al pie.",
    endpointStyles:
      "Busca entre los {count} estilos por nombre, clase de formato de citación, disciplina o región.",
    endpointStyleSlug:
      "Un estilo completo, con ejemplos reales generados por su propia definición CSL.",
    endpointSearch:
      "Búsqueda ordenada por relevancia en todas las páginas del sitio.",
    endpointPages:
      "El catálogo completo del sitio como entradas estructuradas, filtrable por tipo de página.",
    endpointHowToCite:
      "Todas las páginas de orientación de tipo de fuente × estilo, filtrables por cualquiera de los dos.",
    endpointTools:
      "Todas las herramientas gratuitas, con su categoría y palabras clave.",
    endpointGuides: "Todas las guías educativas, con su categoría.",
    endpointConverters:
      "Todas las conversiones de identificadores y formatos que ofrece el sitio.",
    endpointComparisons:
      "Comparaciones de estilos lado a lado, enlazadas a los registros de ambos estilos.",
    endpointFields:
      'Campos académicos, categorías temáticas y regiones, para responder a "¿qué estilo debería usar?".',
    endpointFaq:
      "Respuestas verificadas a preguntas habituales sobre citación, filtrables con q.",
    endpointGlossary:
      "Términos de citación y escritura académica con sus definiciones completas: la respuesta incluye el texto, así que no hay que consultar ninguna página después.",
    endpointIndex:
      "Documento de descubrimiento de endpoints. Empieza aquí si estás explorando.",
    discoveryHeading: "Archivos de descubrimiento",
    discoveryLlms: "Índice curado del sitio escrito para modelos de lenguaje.",
    discoveryLlmsFull:
      "Todas las páginas que publica el sitio, agrupadas por tipo, una línea cada una.",
    discoveryOpenapi: "Descripción OpenAPI 3.1 de toda la API pública.",
    discoveryMcp:
      "Endpoint de MCP (HTTP en streaming). Haz un GET en un navegador para ver una descripción.",
    discoveryWellKnownMcp:
      "Documento de descubrimiento de MCP para agentes que exploran un dominio.",
    discoveryAiPlugin:
      "Manifiesto de complemento heredado, para los frameworks que todavía buscan uno.",
    discoveryMd: "Vista en markdown de cualquier página del sitio.",
    discoverySitemap: "Todas las URL indexables.",
    catalogHeading: "Qué hay en el catálogo",
    catalogIntro:
      "Cada página de abajo se puede consultar como HTML, como markdown bajo `/md/` y como entrada estructurada desde `/api/v1/pages`.",
    catalogStyles:
      "**{count}** estilos de cita, cada uno con ejemplos reales generados a partir de su propia definición CSL",
    catalogTools: "**{count}** herramientas de navegador gratuitas",
    catalogGuides: "**{count}** guías educativas",
    catalogHowToCite: "**{count}** páginas de cómo citar",
    catalogComparisons: "**{count}** comparaciones de estilos",
    catalogConverters: "**{count}** conversores",
    catalogFields: "**{count}** campos académicos",
    catalogStyleLandings:
      "**{count}** centros de estilos principales con las normas de formato completas",
    faqHeading: "Preguntas frecuentes",
    faqFreeQ:
      "¿Es gratuita la API de CitationEasy y necesito una clave de API?",
    faqFreeA:
      "Es gratuita y no hay clave de API. Todos los endpoints bajo /api/v1 no requieren autenticación y tienen CORS abierto, así que puedes llamarlos desde un navegador, un notebook o un agente sin registrarte. No hay límite de solicitudes propio, pero las búsquedas de identificadores pasan por Crossref, Open Library y PubMed, que aplican sus propios límites de uso razonable; agrupa las solicitudes con `inputs` en lugar de lanzar muchas solicitudes en paralelo.",
    faqLlmQ: "¿Las citas las genera un modelo de lenguaje?",
    faqLlmA:
      "No. El formato se genera ejecutando citeproc-js sobre las definiciones oficiales de Citation Style Language, el mismo motor que usan los gestores de referencias. La misma entrada y el mismo estilo siempre producen la misma cadena de texto. Los metadatos bibliográficos provienen de Crossref (DOI), Open Library (ISBN) y PubMed (PMID); CitationEasy no inventa campos, así que lo que falta en el origen falta en el resultado en lugar de suponerse.",
    faqMcpQ: "¿Cómo conecto un agente a CitationEasy mediante MCP?",
    faqMcpA:
      "Apunta cualquier cliente de Model Context Protocol a https://www.citationeasy.com/api/mcp usando el transporte HTTP en streaming. No se necesitan credenciales. En Claude Code: `claude mcp add --transport http citationeasy https://www.citationeasy.com/api/mcp`. El servidor expone herramientas para generar citas, buscar entre los {count} estilos, obtener orientación por tipo de fuente, buscar en el sitio y leer cualquier página como markdown.",
    faqStylesQ: "¿Qué estilos de cita puede generar la API?",
    faqStylesA:
      'Los {count} estilos del repositorio de Citation Style Language, incluidos APA 7, MLA 9, ambos sistemas de Chicago, Harvard, Vancouver, IEEE, AMA y miles de estilos de revistas individuales. Pasa el slug del estilo en el campo `style`. Los slugs sencillos como "chicago" y "harvard" resuelven a la variante CSL correcta, y /api/v1/styles?q= encuentra el resto.',
    faqLlmsTxtQ: "¿Qué es llms.txt y dónde está el de CitationEasy?",
    faqLlmsTxtA:
      "llms.txt es un índice de texto plano de un sitio, escrito para modelos de lenguaje en lugar de navegadores. El de CitationEasy está en /llms.txt; la versión exhaustiva que enumera todas las páginas del sitio está en /llms-full.txt.",
    faqMarkdownQ:
      "¿Puedo leer las páginas de CitationEasy como markdown en lugar de HTML?",
    faqMarkdownA:
      "Sí. Antepón /md/ a cualquier ruta del sitio, por ejemplo /md/citation-style/apa o /md/tools/word-counter. Las páginas basadas en datos devuelven su contenido completo, incluidos los ejemplos reales generados a partir de la propia definición CSL de cada estilo. Las páginas de prosa escritas a mano (guías, hojas de referencia rápida) devuelven una tarjeta resumen más la URL HTML canónica, ya que su texto vive en la propia página.",
    faqReuseQ:
      "¿Puedo usar el resultado de CitationEasy en mi propio producto?",
    faqReuseA:
      "Sí. Una cita con formato es un hecho sobre una fuente; úsala libremente. Las propias definiciones de estilo CSL provienen del proyecto Citation Style Language bajo la licencia CC BY-SA 3.0. Se agradece la atribución a CitationEasy, pero no es obligatoria.",
    missingHeading: "¿Falta algo?",
    missingBody:
      "Si tu agente necesita un endpoint, un campo o un formato que no está aquí, [dínoslo](/contact). ¿Prefieres explorar a mano? [Empieza por el generador de citas](/).",
  },

  // /quiz. The client component reads these as a prop rather than through a context:
  // it is one component, not the generator's nine, so there is nothing to drill through.
  quizPage: {
    metaTitle:
      "¿Qué estilo de cita debo usar? - Cuestionario de estilos gratuito",
    metaDescription:
      "Responde unas preguntas rápidas sobre tu campo de estudio, el tipo de trabajo y tus preferencias para encontrar el mejor estilo de cita para tu trabajo. APA, MLA, Chicago, Vancouver y más.",
    ogTitle: "¿Qué estilo de cita debo usar?",
    ogDescription:
      "Haz nuestro cuestionario gratuito para encontrar el mejor estilo de cita para tu trabajo.",
    breadcrumbHome: "CitationEasy",
    breadcrumbCurrent: "Cuestionario de estilos",
    backLink: "← Volver al generador",
    heading: "¿Qué estilo de cita debo usar?",
    intro:
      "Responde unas preguntas rápidas y te recomendaremos el mejor estilo de cita para tu trabajo.",
    progress: "Pregunta {current} de {total}",
    qDiscipline: "¿Cuál es tu campo de estudio?",
    optSocialScience: "Psicología / Ciencias sociales",
    optHumanities: "Literatura / Humanidades",
    optHistory: "Historia / Historia del arte",
    optMedical: "Medicina / Enfermería / Salud",
    optEngineering: "Ingeniería / Informática",
    optNaturalScience: "Ciencias naturales (biología, química)",
    optLaw: "Derecho",
    optBusiness: "Empresa / Economía",
    qPaperType: "¿Qué tipo de trabajo estás escribiendo?",
    optResearch: "Trabajo de investigación o tesis",
    optReview: "Revisión bibliográfica",
    optReport: "Informe de laboratorio o informe técnico",
    optEssay: "Ensayo o tarea de clase",
    optJournal: "Envío de artículo a una revista",
    qRegion: "¿Dónde está tu institución?",
    optUs: "Estados Unidos",
    optUk: "Reino Unido / Australia",
    optCanada: "Canadá",
    optEurope: "Europa (fuera del Reino Unido)",
    optOther: "Otro",
    qPreference: "¿Tienes preferencia por algún formato de cita?",
    optAuthorDate: "Citas en el texto (Autor, Año)",
    optNumeric: "Referencias numeradas [1]",
    optFootnotes: "Notas al pie",
    optNoPreference: "Sin preferencia",
    styleVancouver: "Vancouver",
    styleAma: "AMA (American Medical Association)",
    styleIeee: "IEEE",
    styleBluebook: "Bluebook",
    styleCse: "CSE (Council of Science Editors)",
    styleChicagoNotes: "Chicago (notas y bibliografía)",
    styleMla: "MLA (9.ª edición)",
    styleApa: "APA (7.ª edición)",
    styleHarvard: "Harvard",
    reasonVancouverMedical:
      "Vancouver es el estándar en las revistas médicas y de ciencias de la salud, y usa referencias numeradas.",
    reasonAma:
      "AMA se usa mucho en revistas médicas y utiliza citas numeradas.",
    reasonIeee:
      "IEEE es el estilo de cita estándar en ingeniería, informática y campos de TI.",
    reasonBluebook:
      "El Bluebook es el estilo de cita por excelencia para la redacción jurídica. El estilo Chicago es la alternativa de uso general más cercana.",
    reasonCse: "CSE es el estándar en biología y ciencias naturales.",
    reasonChicagoHistory:
      "Chicago (notas y bibliografía) es el estándar de oro en historia e historia del arte, y usa notas al pie para las citas.",
    reasonChicagoHumanities:
      "Chicago (notas y bibliografía) funciona bien en humanidades gracias a su enfoque basado en notas al pie.",
    reasonMla: "MLA es el estándar en literatura, lenguas y humanidades.",
    reasonApaBusiness:
      "APA se usa mucho en los campos de empresa, economía y gestión.",
    reasonHarvard:
      "El sistema Harvard es el estilo más común en las universidades del Reino Unido y Australia.",
    reasonChicagoFootnotes:
      "Chicago (notas y bibliografía) usa notas al pie, ideal para citas detalladas.",
    reasonVancouverNumeric:
      "Vancouver usa citas numeradas, lo que mantiene tu texto limpio y legible.",
    reasonApaDefault:
      "APA es el estilo de cita más usado en ciencias sociales, educación y muchas otras disciplinas.",
    recommendLabel: "Te recomendamos",
    generateButton: "Generar citas en {style}",
    retakeButton: "Repetir el cuestionario",
    guidesLink: "Explora nuestras guías de citación para más ayuda →",
    previousButton: "← Pregunta anterior",
  },

  // /search — the server-rendered site search.
  searchPage: {
    metaTitle: "Buscar en CitationEasy",
    metaDescription:
      "Busca en todas las páginas de CitationEasy a la vez: {count} estilos de cita, más de 250 herramientas gratuitas, guías, páginas de cómo citar, comparaciones de estilos, conversores y el glosario.",
    keyword1: "buscar estilos de cita",
    keyword2: "encontrar guía de citación",
    keyword3: "buscador de herramientas de citación",
    ogDescription:
      "Busca en todas las páginas del sitio a la vez: estilos, herramientas, guías, páginas de cómo citar, comparaciones, conversores y términos del glosario.",
    ogImageSubtitle:
      "Todos los estilos de cita, herramientas, guías, páginas de cómo citar, comparaciones, conversores y términos del glosario, en una sola búsqueda.",
    ogKind: "Búsqueda",
    breadcrumb: "Búsqueda",
    heading: "Buscar en CitationEasy",
    intro:
      "Todos los estilos de cita, herramientas, guías, páginas de cómo citar, comparaciones, conversores y términos del glosario del sitio, en una sola búsqueda.",
    inputLabel: "Consulta de búsqueda",
    inputPlaceholder: "p. ej. citar un pódcast en APA, sangría francesa, DOI",
    submit: "Buscar",
    browseHeading: "O explora por sección",
    browseStyles: "Estilos de cita",
    browseStylesNote: "{count} estilos",
    browseTools: "Herramientas gratuitas",
    browseToolsNote: "{count} herramientas",
    browseHowTo: "Cómo citar cualquier fuente",
    browseHowToNote: "{count} páginas",
    browseGuides: "Guías",
    browseGuidesNote: "{count} guías",
    browseGlossary: "Glosario",
    browseGlossaryNote: "{count} términos",
    browseComparisons: "Comparaciones de estilos",
    browseComparisonsNote: "{count} comparaciones",
    browseConverters: "Conversores",
    browseConvertersNote: "{count} conversores",
    browseCheatSheets: "Hojas de referencia rápida",
    browseCheatSheetsNote: "{count} hojas",
    facetsLabel: "Filtrar por tipo de página",
    facetAll: "Todo ({count})",
    noMatches: "Ninguna página coincide con “{query}”.",
    matchesOne: "{count} página coincide con “{query}”.",
    matchesMany: "{count} páginas coinciden con “{query}”.",
    pagerLabel: "Páginas de resultados",
    pagerPrevious: "← Anterior",
    pagerNext: "Siguiente →",
    pagerPosition: "Página {page} de {total}",
    paletteLoading: "Buscando…",
    paletteError:
      "La búsqueda no está disponible en este momento. Inténtalo de nuevo.",
    paletteViewAll: "Ver todos los resultados para «{query}»",
    apiNote:
      "¿Estás creando algo? El mismo índice está disponible en JSON en [/api/v1/search](/api/v1/search?q=podcast) y a través de MCP: consulta [la documentación para agentes](/ai).",
  },

  // /cite-this-page. The six FAQ answers used to exist twice — once in the layout for the
  // FAQPage markup and once in the page for the visible list — which is exactly the drift
  // this dictionary removes: both now read the same keys.
  citeThisPage: {
    metaTitle:
      "Cita esta página - Citas con un clic (marcador y extensión de Chrome)",
    metaDescription:
      "Cita cualquier página web con un clic. Arrastra el marcador gratuito 'Cita esta página' a tu barra de marcadores, o instala la extensión gratuita de Chrome con un botón en la barra de herramientas y citación con clic derecho. Funciona también en Chromebooks y navegadores restringidos.",
    ogTitle: "Marcador 'Cita esta página' y extensión de Chrome",
    ogDescription:
      "Citas de sitios web con un clic desde cualquier página: un marcador de arrastrar y soltar, además de una extensión gratuita de Chrome con citación mediante clic derecho.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbSchemaCurrent: "Marcador Cita esta página",
    appName: "Marcador Cita esta página",
    appDescription:
      "Un marcador gratuito que cita la página web que estás leyendo con un clic: abre CitationEasy con la URL de la página ya rellenada y da formato a la cita en APA, MLA, Chicago y {count} estilos más.",
    breadcrumbLabel: "Ruta de navegación",
    breadcrumbHome: "Inicio",
    breadcrumbCurrent: "Marcador Cita esta página",
    heading: "Cita esta página: marcador y extensión de Chrome",
    intro:
      "Cita cualquier página web con un clic, sin registro. Arrastra el marcador de abajo a tu barra de marcadores (sin instalar nada), o consigue la extensión gratuita de Chrome para tener un botón en la barra de herramientas y citación con clic derecho.",
    dragPrompt: "Arrastra este botón a tu barra de marcadores:",
    buttonTitle: "Arrástrame a tu barra de marcadores",
    buttonLabel: "📑 Cita esta página",
    dontClick:
      "No hagas clic aquí: arrástralo primero a tu barra de marcadores.",
    installHeading: "Cómo instalarlo",
    step1Title: "Muestra tu barra de marcadores",
    step1Body:
      "En la mayoría de los navegadores, pulsa Ctrl+Mayús+B (Cmd+Mayús+B en Mac) para mostrar u ocultar la barra de marcadores y tener dónde soltar el botón.",
    step2Title: "Arrastra el botón hacia arriba",
    step2Body:
      "Haz clic y mantén pulsado el botón 'Cita esta página' de abajo, luego arrástralo hasta tu barra de marcadores y suéltalo. Se convierte en un marcador normal.",
    step3Title: "Haz clic en él en cualquier página",
    step3Body:
      "Mientras lees un artículo, una noticia o una página de una revista, haz clic en tu nuevo marcador. CitationEasy se abre con la URL de esa página ya rellenada y la cita lista para copiar.",
    manualHeading: "¿No puedes arrastrarlo? Añádelo manualmente",
    manualBody:
      'Crea un nuevo marcador, llámalo "Cita esta página" y pega esto como la URL/dirección:',
    howHeading: "Cómo funciona",
    howBody:
      "El marcador no ejecuta ningún código en la página que estás leyendo. Solo envía la dirección web de esa página a CitationEasy, donde nuestro servidor obtiene y analiza los metadatos públicos de la página, el mismo proceso fiable que se usa cuando pegas una URL en el generador a mano. Eso lo mantiene seguro y evita los límites de origen cruzado que dejaron obsoletos a los antiguos marcadores integrados en la página.",
    extensionHeading: "¿Prefieres una extensión de Chrome?",
    extensionBody:
      "La extensión gratuita de CitationEasy añade un botón en la barra de herramientas que cita la pestaña actual, además de opciones en el menú contextual para citar un enlace (o un DOI o ISBN seleccionado) sin abrirlo. Al igual que el marcador, no ejecuta ningún código en las páginas que visitas: solo envía la URL a CitationEasy. Solo necesita los permisos `activeTab` y `contextMenus`, y funciona en Chrome, Edge, Brave y otros navegadores basados en Chromium.",
    downloadButton: "⬇ Descargar la extensión de Chrome (.zip)",
    install1: "Descarga y descomprime la extensión.",
    install2:
      "Abre `chrome://extensions` y activa el **Modo de desarrollador** (arriba a la derecha).",
    install3:
      "Haz clic en **Cargar descomprimida** y selecciona la carpeta descomprimida.",
    install4:
      "Fija el botón de CitationEasy y haz clic en él en cualquier página para citarla.",
    faqHeading: "Preguntas frecuentes",
    faqWhatQ: "¿Qué es el marcador 'Cita esta página'?",
    faqWhatA:
      "Es un pequeño marcador que arrastras a la barra de marcadores de tu navegador. Cuando haces clic en él en cualquier página web, abre CitationEasy en una pestaña nueva con la URL de esa página ya rellenada, así obtienes una cita con formato en un clic, sin copiar y pegar.",
    faqExtensionNeededQ: "¿Necesito instalar una extensión?",
    faqExtensionNeededA:
      "No. Un marcador es solo un marcador, así que no hay nada que instalar ni permisos que conceder. Funciona en Chromebooks escolares restringidos y en la mayoría de los navegadores móviles donde las extensiones están bloqueadas. Si prefieres una extensión, también hay una extensión gratuita de Chrome disponible en esta página.",
    faqChromeQ: "¿Hay una extensión de Chrome?",
    faqChromeA:
      "Sí. La extensión gratuita de Chrome de CitationEasy añade un botón en la barra de herramientas que cita la pestaña actual, además de opciones en el menú contextual para citar un enlace o un DOI/ISBN seleccionado sin abrirlo. No ejecuta ningún código en las páginas que visitas y solo necesita los permisos activeTab y contextMenus. Descárgala desde esta página, cárgala desde chrome://extensions y funciona en Chrome, Edge, Brave y otros navegadores basados en Chromium.",
    faqAddQ: "¿Cómo añado el marcador?",
    faqAddA:
      "Asegúrate de que tu barra de marcadores esté visible y luego arrastra el botón 'Cita esta página' de esta página hasta ella. Si no puedes arrastrarlo (por ejemplo, en el móvil), crea un marcador nuevo manualmente y pega el código del marcador como la URL.",
    faqSafeQ: "¿Es seguro? ¿Lee la página en la que estoy?",
    faqSafeA:
      "Sí, es seguro. El marcador no ejecuta ningún código en la página que estás leyendo: solo envía la dirección web de esa página a CitationEasy, donde nuestro servidor obtiene y analiza los metadatos públicos de la página, exactamente igual que cuando pegas una URL manualmente.",
    faqOtherSourcesQ: "¿Funciona con fuentes distintas de páginas web?",
    faqOtherSourcesA:
      "El marcador es para páginas web, pero el enlace subyacente también acepta un DOI, ISBN, PMID o ID de arXiv mediante el parámetro ?source=, así que un enlace para compartir de 'cita esto' también funciona para artículos de revista y libros.",
    relatedHeading: "Herramientas relacionadas",
    relatedGenerator: "Generador de citas",
    relatedHowToCite: "Cómo citar un sitio web",
    relatedTools: "Todas las herramientas",
  },

  // /shared-bibliography — opens a bibliography that travelled inside a share link.
  sharedBibliographyPage: {
    metaTitle: "Bibliografía compartida - Ver e importar citas compartidas",
    metaDescription:
      "Abre una bibliografía que alguien compartió contigo desde CitationEasy. Consulta las citas con formato, cópialas o importa toda la lista a tu propia bibliografía: gratis, sin cuenta, y los datos viven en el propio enlace.",
    ogTitle: "Bibliografía compartida",
    ogDescription:
      "Consulta e importa una bibliografía compartida. Las citas viajan dentro del enlace: no se guarda nada en un servidor y los enlaces nunca caducan.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbLabel: "Ruta de navegación",
    breadcrumbHome: "Inicio",
    breadcrumbCurrent: "Bibliografía compartida",
    heading: "Bibliografía compartida",
    intro:
      "Alguien compartió estas citas contigo. Cópialas o importa toda la lista a tu propia bibliografía de CitationEasy con un clic.",
    loading: "Abriendo la bibliografía compartida…",
    copyAll: "Copiar todo",
    copied: "Copiado",
    copyFailed: "Error al copiar",
    citationOne: "cita",
    citationMany: "citas",
    addAll: "Añadir todo a mi bibliografía",
    added: "{count} añadidas a tu bibliografía",
    skippedOne: "{count} duplicado omitido",
    skippedMany: "{count} duplicados omitidos",
    openGenerator: "abrir el generador",
    emptyHeading: "No hay ninguna bibliografía compartida en este enlace",
    emptyBody:
      "Esta página muestra bibliografías compartidas desde CitationEasy. Para compartir la tuya, abre tu bibliografía en el [generador de citas](/), elige **Exportar → Copiar enlace para compartir** y envía el enlace a quien quieras. Las citas viajan dentro del propio enlace: no se guarda nada en un servidor, así que los enlaces siguen funcionando para siempre y tus datos permanecen privados.",
    decodeError: "No se pudo abrir este enlace compartido.",
  },

  // The 19 source types the 15 detailed styles show worked examples for
  // (`DetailedStyleData.examples[].sourceType`). 75 rows, 19 distinct values — so this
  // is a vocabulary, not per-page copy, and it belongs here rather than repeated in
  // every locale's `src/content/style-pages/<locale>.json`.
  //
  // Two casings for the same reason `howToCitePage.refList*Lower` has two: the pages
  // print the label as a heading ("Book") and inside a sentence ("a sample APA entry
  // for a book"). Lower-casing the translated noun in code is wrong in German, where
  // every one of these is capitalised, so the lower form is a real key a translator
  // fills rather than a `.toLowerCase()` call.
  //
  // The citations themselves — `referenceEntry` and `inTextCitation` — are NOT here and
  // are not translatable anywhere: they are worked examples in a specific style's own
  // punctuation. See `src/lib/localized-content.ts`.
  sourceTypes: {
    book: "Libro",
    bookLower: "libro",
    bookChapter: "Capítulo de libro",
    bookChapterLower: "capítulo de libro",
    case: "Caso",
    caseLower: "caso",
    conferencePaper: "Ponencia de congreso",
    conferencePaperLower: "ponencia de congreso",
    courtCase: "Caso judicial",
    courtCaseLower: "caso judicial",
    federalStatute: "Ley federal",
    federalStatuteLower: "ley federal",
    governmentDocument: "Documento gubernamental",
    governmentDocumentLower: "documento gubernamental",
    governmentReport: "Informe gubernamental",
    governmentReportLower: "informe gubernamental",
    journalArticle: "Artículo de revista",
    journalArticleLower: "artículo de revista",
    lawReviewArticle: "Artículo de revista jurídica",
    lawReviewArticleLower: "artículo de revista jurídica",
    legislation: "Legislación",
    legislationLower: "legislación",
    newspaperArticle: "Artículo de periódico",
    newspaperArticleLower: "artículo de periódico",
    patent: "Patente",
    patentLower: "patente",
    podcast: "Pódcast",
    podcastLower: "pódcast",
    preprint: "Preprint",
    preprintLower: "preprint",
    standard: "Norma",
    standardLower: "norma",
    thesis: "Tesis",
    thesisLower: "tesis",
    website: "Página web",
    websiteLower: "página web",
    youtubeVideo: "Video de YouTube",
    youtubeVideoLower: "video de YouTube",
  },

  // Chrome shared by the five per-style guide routes — /examples, /format,
  // /in-text-citation, /reference-page and /annotated-bibliography, 15 styles each.
  //
  // One section rather than five copies: the CTA, the back link and the cross-links
  // between the five pages are the same sentences on every one of them, and duplicating
  // them per route would be five identical translation units instead of one.
  styleGuidePages: {
    backToStyle: "Volver a {style}",
    backToGenerator: "Volver al generador de citas",
    ctaHeading: "Genera citas en {style} automáticamente",
    ctaButton: "Cita en {style} ahora →",
    faqHeading: "Preguntas frecuentes",
    resourcesHeading: "Más recursos",
    relatedHeading: "Guías de {style} relacionadas",
    resourceGenerator: "Generador de citas gratuito ({count} estilos)",
    resourceTools: "Herramientas de escritura gratuitas",
    resourceGuides: "Todas las guías de citación",
    resourceStyleGenerator: "Generador de citas en {style}",
    resourceExamples: "Ejemplos de citas en {style}",
    resourceFormat: "Guía de formato de {style}",
    resourceInText: "Guía de citas en el texto en {style}",
    resourceAnnotatedBib: "Guía de bibliografía anotada en {style}",
    resourceReferencePage: "Guía de {list} en {style}",
    notFoundTitle: "No encontrado",
  },

  // /examples/<style> — the copy-ready worked examples for all 15 detailed styles.
  examplesPage: {
    metaTitle:
      "Ejemplos de citas en {style} - Todos los tipos de fuente [2026]",
    metaDescription:
      "Consulta ejemplos de citas en {style} ({edition}) con el formato correcto para libros, artículos de revista, páginas web y más. Entradas de {list} listas para copiar y citas en el texto.",
    ogTitle: "Ejemplos de citas en {style}",
    ogDescription:
      "Entradas en {style} listas para copiar para cada tipo de fuente habitual.",
    ogImageTitle: "Ejemplos de citas en {style}",
    ogKind: "{style} {edition}",
    keywordExamples: "ejemplos de citas en {style}",
    keywordReference: "ejemplos de referencias en {style}",
    keywordInText: "ejemplos de citas en el texto en {style}",
    keywordList: "ejemplos de {listLower} en {style}",
    keywordHowTo: "cómo citar en {style}",
    breadcrumb: "Ejemplos de citas en {style}",
    heading: "Ejemplos de citas en {style}",
    lead: "Consulta ejemplos de citas en {style} ({edition}) con el formato correcto para {count} tipos de fuente habituales. Cada ejemplo incluye tanto la entrada de {listLower} como la cita en el texto correspondiente.",
    entryLabel: "Entrada de {list}:",
    inTextLabel: "Cita en el texto:",
    ctaBody:
      "Olvídate del formato manual. Pega un DOI, una URL o un ISBN en CitationEasy y obtén una cita en {style} perfectamente formateada en segundos.",
    faqLookQ: "¿Cómo es una cita en {style}?",
    faqLookA:
      "Una cita en {style} incluye una entrada de {listLower} con los detalles completos de la fuente y una cita en el texto. El formato varía según el tipo de fuente. Por ejemplo, una cita de un libro en {style} ({edition}) incluye el autor, el año, el título y la editorial.",
    faqHowManyQ: "¿Cuántos ejemplos de citas en {style} hay disponibles?",
    faqHowManyA:
      "CitationEasy ofrece ejemplos de citas en {style} para {count} tipos de fuente, entre ellos {types}. Cada ejemplo incluye tanto la entrada de {listLower} como la cita en el texto correspondiente.",
    faqCopyQ: "¿Puedo copiar estos ejemplos de citas en {style}?",
    faqCopyA:
      "Sí, los ejemplos en {style} de esta página muestran el formato exacto para cada tipo de fuente. Puedes usarlos como plantillas para tus propias citas. Para un formato automático, usa nuestro generador de citas gratuito en {style}.",
  },

  // /format/<style> — font, margins, spacing, title page and headers. The VALUES those
  // labels take are per-style prose and live in the overlay, not here.
  formatPage: {
    metaTitle: "Guía de formato {style} - Normas de formato del trabajo [2026]",
    metaDescription:
      "Guía completa de formato del trabajo en {style} ({edition}). Aprende la fuente, los márgenes, el espaciado, la portada, los encabezados y las normas de formato de {listLower} correctos.",
    ogTitle: "Formato del trabajo en {style}",
    ogDescription:
      "Márgenes, fuente, espaciado y portada de un trabajo en {style}.",
    ogImageTitle: "Formato del trabajo en {style}",
    ogImageSubtitle: "Márgenes de {margins} · {spacing}",
    ogKind: "{style} {edition}",
    keywordFormat: "formato {style}",
    keywordPaperFormat: "formato del trabajo en {style}",
    keywordRules: "normas de formato {style}",
    keywordMargins: "márgenes fuente espaciado {style}",
    keywordList: "formato de {listLower} en {style}",
    breadcrumb: "Guía de formato {style}",
    heading: "Guía de formato {style}",
    lead: "Sigue estas normas de formato del trabajo en {style} ({edition}) para asegurarte de que tu documento cumple los requisitos exigidos. Esta guía cubre la fuente, los márgenes, el espaciado, la portada, los encabezados y el formato de {listLower}.",
    paperHeading: "Formato del trabajo",
    paperIntro:
      "A continuación se muestran los requisitos de formato básicos para trabajos escritos en estilo {style} ({edition}).",
    labelFont: "Fuente",
    labelFontSize: "Tamaño de fuente",
    labelMargins: "Márgenes",
    labelSpacing: "Espaciado",
    labelTitlePage: "Portada",
    labelHeaders: "Encabezados/Números de página",
    listHeading: "Formato de {list}",
    listIntro:
      "La sección {listLower} debe seguir estas normas para tener el formato correcto en estilo {style}:",
    ctaBody:
      "Deja que CitationEasy se encargue del formato. Pega un DOI, una URL o un ISBN y obtén una cita en {style} perfectamente formateada en segundos.",
    faqFontQ: "¿Qué fuente y tamaño debo usar para el formato {style}?",
    faqFontA:
      "{style} ({edition}) exige {font}. El tamaño de fuente estándar es {fontSize}.",
    faqMarginsQ: "¿Cuáles son los requisitos de márgenes para {style}?",
    faqMarginsA:
      "Los trabajos en {style} exigen {margins}. Esto se aplica a los márgenes superior, inferior, izquierdo y derecho de cada página.",
    faqTitlePageQ: "¿{style} exige una portada?",
  },

  // /in-text-citation/<style> — the six in-text forms. The forms themselves
  // ("(Smith, 2024)") are per-style prose in the overlay.
  inTextCitationPage: {
    metaTitle:
      "Guía de citas en el texto en {style} - Normas y ejemplos [2026]",
    metaDescription:
      "Aprende a dar formato a las citas en el texto en {style} ({edition}). Normas para un autor, varios autores, citas textuales, sin autor y sin fecha, con ejemplos.",
    ogTitle: "Citas en el texto en {style}",
    ogDescription:
      "Todas las formas de cita en el texto en {style}, con ejemplos.",
    ogImageTitle: "Citas en el texto en {style}",
    ogImageSubtitle: "Un autor {singleAuthor} · tres o más {threeOrMore}",
    ogKind: "{style} {edition}",
    keywordInText: "cita en el texto en {style}",
    keywordExamples: "ejemplos de citas en el texto en {style}",
    keywordParenthetical: "cita parentética en {style}",
    keywordRules: "normas de citación {style}",
    keywordHowTo: "cómo citar en el texto en {style}",
    breadcrumb: "Citas en el texto en {style}",
    heading: "Citas en el texto en {style}",
    lead: "Domina las citas en el texto en {style} ({edition}) con esta guía completa. Aprende el formato correcto para cada situación, incluidos uno y varios autores, citas textuales y fuentes con información incompleta.",
    rulesHeading: "Normas de citas en el texto",
    rulesIntro:
      "A continuación se muestran las normas de citas en el texto en {style} para cada situación habitual que encontrarás al escribir tu trabajo.",
    labelSingleAuthor: "Un autor",
    labelTwoAuthors: "Dos autores",
    labelThreeOrMore: "Tres autores o más",
    labelDirectQuote: "Cita textual",
    labelNoAuthor: "Sin autor",
    labelNoDate: "Sin fecha",
    examplesHeading: "Ejemplos rápidos",
    examplesIntro:
      "Estos son ejemplos reales de citas en el texto en {style} para distintos tipos de fuente:",
    ctaBody:
      "Deja de preocuparte por el formato de las citas en el texto. Pega un DOI, una URL o un ISBN en CitationEasy y obtén al instante tanto la entrada de {listLower} como la cita en el texto.",
    faqFormatQ: "¿Cómo doy formato a una cita en el texto en {style}?",
    faqFormatA:
      "Para un solo autor en {style}, usa el formato {singleAuthor}. Para dos autores, usa {twoAuthors}. Para tres o más autores, usa {threeOrMore}.",
    faqQuoteQ: "¿Cómo cito una cita textual en {style}?",
    faqQuoteA:
      "Para citas textuales en {style} ({edition}), usa el formato: {directQuote}.",
    faqMissingQ: "¿Qué hago si no hay autor o no hay fecha en {style}?",
    faqMissingA:
      "Cuando no hay autor en {style}, usa: {noAuthor}. Cuando no hay fecha, usa: {noDate}.",
  },

  // /reference-page/<style> — how to build the list at the back of the paper.
  //
  // The five `mistake*` strings carry `**bold**` inline markup because the lead-in of
  // each bullet is emphasised inside the sentence; they render through `InlineText`.
  referenceListPage: {
    metaTitle: "Guía de {list} en {style} - Normas de formato [2026]",
    metaDescription:
      "Aprende a dar formato a la sección {listLower} en {style} ({edition}). Normas de formato paso a paso, ejemplos y errores comunes que debes evitar en {fullName}.",
    ogTitle: "{listLower} en {style}",
    ogDescription: "Cómo elaborar la sección {list} en {style}.",
    ogImageTitle: "{list} en {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Cómo dar formato a la sección {list} en {style}",
    heading: "Cómo dar formato a la sección {list} en {style}",
    lead: "La sección {listLower} es una de las partes más importantes de cualquier trabajo académico con formato {style} ({edition}). Ofrece a los lectores los detalles completos de cada fuente que citaste, para que puedan localizarla y verificarla. Acertar con el formato es esencial para la credibilidad académica. Esta guía cubre las normas exactas, muestra ejemplos reales y señala los errores más comunes que cometen los estudiantes.",
    rulesHeading: "Normas de formato",
    rulesIntro:
      "Sigue estas normas al crear la sección {listLower} en {style}:",
    exampleHeading: "Ejemplo de {list}",
    exampleIntro:
      "Así es como se ve la sección {listLower} en {style} con entradas para distintos tipos de fuente:",
    mistakesHeading: "Errores comunes que debes evitar",
    mistakesIntro:
      "Estos son los cinco errores de formato más frecuentes que cometen los estudiantes al crear la sección {listLower} en {style}:",
    mistakeIndent:
      "**Sangría francesa incorrecta o ausente:** cada entrada debe tener la primera línea alineada a la izquierda y las líneas siguientes con una sangría de 0,5 pulgadas (1,27 cm). Usa las opciones de párrafo de tu procesador de texto en lugar de pulsar la tecla Tabulador.",
    mistakeOrder:
      '**Orden alfabético incorrecto:** las entradas deben organizarse según las normas de {style}. Ignora artículos como "A," "An" y "The" (o "el," "la," "los," "las") al alfabetizar. En los estilos numéricos, ordena las entradas según el orden en que aparecen por primera vez en el texto.',
    mistakeSpacing:
      "**Espaciado incoherente:** revisa el interlineado en toda la sección {listLower}. Mezclar espaciado sencillo y doble es un error común que pasa fácilmente desapercibido.",
    mistakeItalics:
      "**Uso incorrecto de la cursiva:** pon en cursiva los títulos de obras independientes, como libros y revistas, pero no los títulos de artículos o capítulos. Consulta las normas de {style} para saber exactamente qué elementos deben ir en cursiva.",
    mistakeLinks:
      "**DOI y URL ausentes o rotos:** incluye siempre un DOI cuando esté disponible, con formato de enlace en el que se pueda hacer clic. Comprueba cada URL antes de entregar tu trabajo para asegurarte de que lleva a la fuente correcta.",
    ctaHeading: "Genera la sección {list} automáticamente",
    ctaBody:
      "Olvídate del formato manual. Pega un DOI, una URL o un ISBN en CitationEasy y obtén una cita en {style} perfectamente formateada en segundos, lista para la sección {listLower}.",
    faqWhatQ: "¿Qué es la sección {list} en {style}?",
    faqWhatA:
      "La sección {list} en {style} es la parte final de tu trabajo que enumera todas las fuentes que citaste en el texto, con el formato de las normas de {fullName} ({edition}). Permite a los lectores localizar y verificar cada fuente que mencionaste.",
    faqFormatQ: "¿Cómo doy formato a la sección {listLower} en {style}?",
    faqFormatA:
      'Empieza en una página nueva, titúlala "{list}" y enumera todas las fuentes que citaste en tu trabajo. Sigue las normas específicas de {style} para la sangría francesa, el espaciado, el orden alfabético o numérico, y la cursiva.',
    faqDifferenceQ:
      "¿Cuál es la diferencia entre la sección {listLower} y una bibliografía?",
    faqDifferenceA:
      'La sección {listLower} incluye solo las fuentes que citaste directamente en tu trabajo, mientras que una bibliografía también puede incluir fuentes que consultaste pero no citaste. En {style} ({edition}), la lista al final del trabajo se llama "{list}."',
  },

  // /annotated-bibliography/<style>. `whatTypes` carries `**bold**` inline markup.
  //
  // `exampleAnnotation` is a specimen annotation, not a citation: it is ordinary prose
  // about an imaginary source and translates like any other paragraph. The reference
  // entry printed above it does not — that comes from `DETAILED_STYLES` and stays as
  // the style wrote it.
  annotatedBibPage: {
    metaTitle: "Guía y ejemplos de bibliografía anotada en {style} [2026]",
    metaDescription:
      "Aprende a escribir una bibliografía anotada en {style} ({edition}). Incluye normas de formato, ejemplos de entradas y consejos para bibliografías anotadas en {fullName}.",
    ogTitle: "Bibliografía anotada en {style}",
    ogDescription: "Cómo dar formato a una bibliografía anotada en {style}.",
    ogImageTitle: "Bibliografía anotada en {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Bibliografía anotada en {style}",
    heading: "Bibliografía anotada en {style}",
    lead: "Una bibliografía anotada es una lista de citas de fuentes en la que cada una incluye un breve párrafo descriptivo o valorativo. En formato {style} ({edition}), cada entrada comienza con una referencia correctamente formateada, seguida de una anotación que resume, evalúa o reflexiona sobre la fuente. Esta guía te explica las normas, el formato y los ejemplos que necesitas para crear una bibliografía anotada en {style} pulida.",
    whatHeading: "¿Qué es una bibliografía anotada?",
    whatBody:
      "Una bibliografía anotada va más allá de la sección {listLower} estándar al añadir un breve párrafo después de cada cita. Esta anotación ofrece a los lectores una idea del contenido, la calidad y la relevancia de la fuente. Los profesores suelen asignar bibliografías anotadas para ayudar a los estudiantes a evaluar sus fuentes de forma crítica y demostrar cómo contribuye cada una a su tema de investigación.",
    whatTypes:
      "Hay tres tipos habituales de anotaciones: las **descriptivas**, que resumen la fuente; las **valorativas**, que evalúan la calidad y fiabilidad del trabajo; y las de **combinación**, que hacen ambas cosas. Las instrucciones de tu tarea deben indicar qué tipo usar.",
    rulesHeading: "Normas de bibliografía anotada en {style}",
    rulesIntro:
      "Sigue estas normas al dar formato a una bibliografía anotada en {fullName}:",
    exampleHeading: "Ejemplo de entrada de bibliografía anotada",
    exampleIntro:
      "A continuación, un ejemplo de entrada de bibliografía anotada en {style} para un/a {sourceType}:",
    exampleAnnotation:
      "Esta fuente ofrece una visión general completa del tema y presenta argumentos bien respaldados por evidencia empírica. El autor recurre a una amplia gama de fuentes primarias y secundarias para construir un argumento convincente. La metodología es rigurosa y los resultados se presentan con claridad, lo que convierte este trabajo en un recurso valioso para comprender el tema. Esta fuente resulta especialmente útil para establecer el marco teórico del proyecto de investigación y ofrece perspectivas que complementan otros trabajos de la bibliografía.",
    tipsHeading: "Consejos para escribir anotaciones en {style}",
    tipSummarize:
      "Empieza resumiendo la fuente en dos o tres frases y añade después tu evaluación de sus fortalezas, limitaciones y relevancia.",
    tipTone:
      "Usa la tercera persona y un tono académico en toda la anotación. Evita el lenguaje coloquial o las reflexiones en primera persona, salvo que tu profesor lo pida.",
    tipLength:
      "Mantén cada anotación entre 100 y 200 palabras. Sé conciso y céntrate en los aspectos más importantes de la fuente.",
    tipRelevance:
      "Explica cómo encaja la fuente en tu investigación general. Describe qué perspectiva o evidencia única aporta.",
    tipCitation:
      "Asegúrate de que la parte de la cita de cada entrada siga exactamente las normas de formato de {fullName} antes de escribir la anotación.",
    ctaHeading: "Crea tu bibliografía anotada automáticamente",
    ctaBody:
      "Usa el creador de bibliografías anotadas de CitationEasy para generar citas en {style} correctamente formateadas y luego añade tus anotaciones.",
    ctaButton: "Abrir el creador de bibliografías anotadas →",
    faqWhatQ: "¿Qué es una bibliografía anotada en {style}?",
    faqWhatA:
      "Una bibliografía anotada en {style} ({edition}) es una lista de citas con el formato de {fullName}, cada una seguida de un breve párrafo que resume, evalúa o reflexiona sobre la fuente. Ayuda a los lectores a entender la relevancia y la calidad de cada fuente.",
    faqLengthQ: "¿Cuánto deben durar las anotaciones?",
    faqLengthA:
      "Las anotaciones suelen tener entre 100 y 200 palabras. Algunas tareas pueden exigir anotaciones más cortas o más largas según la profundidad de análisis esperada. Consulta siempre las indicaciones de tu profesor para conocer los requisitos exactos de longitud.",
    faqIncludeQ: "¿Qué debe incluir una anotación?",
    faqIncludeA:
      "Una anotación debe incluir un breve resumen del argumento o los hallazgos principales de la fuente, una evaluación de su credibilidad y utilidad, y una reflexión sobre cómo encaja en tu investigación. Algunas anotaciones son puramente descriptivas, mientras que otras incluyen un análisis crítico.",
  },

  guidePage: {
    byline: "Por {author}",
    published: "Publicado el {date}",
    updated: "Actualizado el {date}",
    readingTime:
      "one:{count} minuto de lectura|other:{count} minutos de lectura",
    wordCount: "one:{count} palabra|other:{count} palabras",
  },

  // /guides hub, /guides/<category> index pages, and the chrome around each guide
  guideLibrary: {
    eyebrow: "Biblioteca de guías",
    browseByCategory: "Explorar por categoría",
    recentlyUpdated: "Actualizadas recientemente",
    allGuides: "Todas las guías",
    guideCount: "one:{count} guía|other:{count} guías",
    updatedOn: "Actualizada el {date}",
    minRead: "one:{count} min de lectura|other:{count} min de lectura",
    onThisPage: "En esta página",
    viewAllInCategory: "Todas las guías de {category}",
    moreInCategory: "Más en {category}",
    aboutThisGuide: "Sobre esta guía",
    provenance:
      "Escrita por {author}. Publicada el {published}; última actualización el {updated}.",
    categoryIntroSuffix:
      "Todas las guías son gratuitas, están escritas para estudiantes e investigadores y llevan fecha para que sepas cuándo se revisaron por última vez.",
    photoCredit: "Foto: {author}, {source} ({license})",
    imageAlt: "Fotografía que ilustra las guías de {category}",
    imageSourceCommons: "Wikimedia Commons",
    apaStyleName: "Estilo APA",
    apaStyleDescription:
      "Cómo citar sitios web, artículos de revista, libros, vídeos y más en APA 7.ª edición, con ejemplos de lista de referencias y citas en el texto.",
    mlaStyleName: "Estilo MLA",
    mlaStyleDescription:
      "Cómo citar cualquier fuente habitual en MLA 9.ª edición, desde páginas web y artículos hasta poemas, obras de teatro, películas y materiales de curso.",
    chicagoStyleName: "Estilo Chicago",
    chicagoStyleDescription:
      "Guías del Manual de Estilo de Chicago con citas en notas-bibliografía y autor-fecha para libros, artículos, archivos, documentos oficiales y medios.",
    styleComparisonsName: "Comparaciones de estilos",
    styleComparisonsDescription:
      "Comparaciones lado a lado de APA, MLA, Chicago, Harvard, Vancouver, IEEE y más, con consejos sobre qué estilo espera tu disciplina.",
    researchMethodsName: "Métodos de investigación",
    researchMethodsDescription:
      "Cómo planificar un trabajo de investigación, revisar la literatura, tomar notas, evaluar fuentes, evitar el plagio y elaborar una bibliografía.",
    academicWritingName: "Escritura académica",
    academicWritingDescription:
      "Guías de redacción para resúmenes, introducciones, conclusiones, tesis, secciones de metodología y prosa académica clara.",
    fieldSpecificName: "Por disciplina",
    fieldSpecificDescription:
      "Guías de citación por disciplina: psicología, historia, literatura, medicina, ingeniería, derecho, enfermería y más.",
    sourceEvaluationName: "Evaluación de fuentes",
    sourceEvaluationDescription:
      "Cómo juzgar la credibilidad, distinguir fuentes académicas de divulgativas, usar Google Scholar y bases de datos, y detectar sesgos.",
    citationHelpName: "Ayuda con citas",
    citationHelpDescription:
      "Respuestas a los casos difíciles: sin autor o fecha, varios autores, PDF, herramientas de IA, casos legales e importación o exportación de citas.",
  },

  common: {
    backToGuides: "← Volver a las guías",
    free: "Gratis",
    noSignup: "Sin registro",
    backToHome: "Volver al generador de citas",
    inEnglish: "En inglés",
    viewInEnglish: "Ver en inglés",
    availableInEnglish:
      "Esta página está disponible en inglés con más detalle.",
    otherLanguages: "Otros idiomas",
    skipToContent: "Saltar al contenido",
    cookieNotice:
      "Usamos cookies con fines analíticos y publicitarios para mantener CitationEasy gratuito.",
    privacyPolicy: "Política de privacidad",
    accept: "Aceptar",
    decline: "Rechazar",
    advertisement: "Publicidad",
  },
  legalPage: {
    backLink: "← Volver al generador",
    effective: "Fecha de entrada en vigor:",
    updated: "Última actualización:",
  },
};

export default es;
